import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import * as THREE from '@phdsoft/three';
import { Util } from '@phdsoft/common';
import { Injectable } from '@angular/core';
import { Vector3 } from '@phdsoft/three';
import { Graphics } from './graphics';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { WorkableElement } from 'src/app/platform/busines-data/SectionElements/WorkableElement';
import { GaugingElement } from 'src/app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants';
import { ThreeJsCleanup } from 'src/app/shared/utils/threejs-cleanup.util';
import { StructuralElement } from '@app/platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { RepairGeometryUtils } from './repair-geometry-utils';
import { RepairGeometryClipper } from './repair-geometry-clipper';
import { RepairRoundedCorners } from './repair-rounded-corners';
import { Rectd, INITIAL_RECTD_STATE } from './repair-selection-state';
import { RepairOrthoFrame, OrthoFrameCandidate } from './repair-ortho-frame';
import { GraphicsRenderRuler } from './graphics-render-ruler';

import { Section } from 'src/app/platform/busines-data/Framework/Section';


@Injectable({ providedIn: 'root' })
export class GraphicsRenderRepair {
  sceneObjs: any[];
  distancePoints = [];
  private asset: Asset;

  // Pooled objects for screenToLocal to avoid per-call allocations
  private _pooledRaycaster = new THREE.Raycaster();
  private _pooledNdc = new THREE.Vector2();

  // Reusable BufferGeometry for the visible drag rectangle
  private _dragRectPositions = new Float32Array(12); // 4 vertices x 3 components
  private _dragRectGeometry: THREE.BufferGeometry | null = null;
  private _dragRectMesh: THREE.Mesh | null = null;

  constructor(
    private graphics: Graphics,
    private graphicsRenderRuler: GraphicsRenderRuler,
  ) { }

  // rectangle decal (for cloning)
  public static _rectd: Rectd = {
    ...INITIAL_RECTD_STATE,
    decalGroup: new THREE.Group(),
    inspections: new Array<GaugingElement>()
  };

  private resetRectdGeometryData(): void {
    this.graphics.isDragMode = false;
    if (this._dragRectMesh) {
      this.graphics.camera.remove(this._dragRectMesh);
    }
    if (this._dragRectGeometry) {
      this._dragRectGeometry.dispose();
    }
    this._dragRectGeometry = null;
    this._dragRectMesh = null;

    // Dispose of objects that need cleanup
    this.graphicsRenderRuler.clearAreaDimensionRulers();
    ThreeJsCleanup.disposeObject(this.rectd.decalGroup);
    this.rectd.decalGroup = new THREE.Group();

    ThreeJsCleanup.disposeObject(this.rectd.geom);
    this.rectd.geom = undefined;

    ThreeJsCleanup.disposeObject(this.rectd.geom1);
    this.rectd.geom1 = undefined;

    ThreeJsCleanup.disposeObject(this.rectd.geom2);
    this.rectd.geom2 = undefined;

    ThreeJsCleanup.disposeObject(this.rectd.geom3);
    this.rectd.geom3 = undefined;

    ThreeJsCleanup.disposeObject(this.rectd.geom4);
    this.rectd.geom4 = undefined;

    // Don't use ThreeJsCleanup.disposeObject on mesh — it disposes the shared selectionBoxMaterial
    if (this.rectd.mesh) {
      if (this.rectd.mesh.parent) this.rectd.mesh.parent.remove(this.rectd.mesh);
      if (this.rectd.mesh.geometry) this.rectd.mesh.geometry.dispose();
    }
    this.rectd.mesh = undefined;

    if (this.rectd.meshs) {
      for (const mesh of this.rectd.meshs) {
        ThreeJsCleanup.disposeObject(mesh);
      }
    }
    this.rectd.meshs = undefined;

    if (this.rectd.meshsAfterCut) {
      for (const meshArray of this.rectd.meshsAfterCut) {
        if (Array.isArray(meshArray)) {
          for (const mesh of meshArray) {
            ThreeJsCleanup.disposeObject(mesh);
          }
        } else {
          ThreeJsCleanup.disposeObject(meshArray);
        }
      }
    }
    this.rectd.meshsAfterCut = undefined;

    this.rectd.inspections = new Array<GaugingElement>();
    this.rectd.targets = undefined;

    this.disposeOrthoPreview(true); // terminal: also frees the drag's fill clones
    this.orthoFrame = null;
    this.orthoFrameAttempted = false;
  }

  /**
   * Elements eligible for the current repair operation
   * (also used as raycast candidates for the orthographic projection frame).
   */
  private getRepairCandidateElements(): WorkableElement[] {
    if (this.focusedPlate != null) {
      return [this.focusedPlate];
    }
    if (this.focusedSection != null) {
      // New-format REGIONS hides the section groups (realVisibility is false for every element),
      // but a plate whose own mesh is hidden (e.g. a type filtered out by a view mode) must not be
      // cut either: use the same rule as the preview, the frame and the ruler. Elsewhere the
      // predicate is realVisibility, as before.
      // was: const skipVisibility = this.asset.isNewModelMode && this.asset.mode === 'REGIONS';
      // was:   .filter(x => skipVisibility || x.realVisibility);
      return [...this.focusedSection.surfaceElements, ...this.focusedSection.linearElements]
        .filter(x => this.isAreaToolTargetShown(x));
    }
    // User rule: in new-format REGIONS no area is inserted and nothing is projected until a
    // section (or plate) is selected. With nothing focused every visible section of every visible
    // region would be a candidate, and the ortho prism has no depth limit (MV22 Engine Room: an
    // 80 px area cut 49 plates in 14 sections). This keeps the preview, the insertion ortho frame
    // and the clipping loop empty there.
    // was: return this.asset.getStructureAndRepairsElements(true);
    // was: return this.isNewModelRegions
    // was:   ? this.asset.getStructureAndRepairsElements(false).filter(el => this.isAreaToolTargetShown(el))
    // was:   : this.asset.getStructureAndRepairsElements(true);
    return this.isNewModelRegions ? [] : this.asset.getStructureAndRepairsElements(true);
  }

  /**
   * Orthographic projection frame candidates among `elements`: the shown target-type elements
   * with a mesh. Shared by the live preview and the insertion so both build the same frame;
   * takes the list so updateOrthoPreview builds the repair candidates only once per move.
   */
  private orthoFrameCandidates(elements: WorkableElement[]): OrthoFrameCandidate[] {
    return elements
      .filter(el => el && this.isAreaToolTargetShown(el) && el.TypeId === GraphicsRenderRepair.targetTypeId && el.SurfaceDesign)
      .map(el => ({ element: el, mesh: el.SurfaceDesign }));
  }

  /**
   * Builds (or rebuilds) a world-space preview of the area that will actually be inserted,
   * by projecting the current drawn shape with the same orthographic frame used on drop.
   * Shown only when ortho projection is enabled and the frame can be built — otherwise the
   * caller keeps the legacy flat screen-space preview. Returns true when a projected
   * preview is on screen.
   */
  private updateOrthoPreview(): boolean {
    this.disposeOrthoPreview(); // per-move rebuild: keep the fill clones (no shader recompile)
    if (!GraphicsRenderRepair.useOrthoProjection || !this.asset) {
      return false;
    }

    // built once per update: the frame candidates and (without a focused plate) the proxy sources
    const repairCandidates = this.getRepairCandidateElements();
    // was: const candidates: OrthoFrameCandidate[] = this.getRepairCandidateElements()
    // filter moved to orthoFrameCandidates() with realVisibility -> isAreaToolTargetShown; base was:
    // was:   .filter(el => el && el.realVisibility && el.TypeId === GraphicsRenderRepair.targetTypeId && el.SurfaceDesign)
    const candidates = this.orthoFrameCandidates(repairCandidates);
    if (candidates.length === 0) {
      return false;
    }

    const frame = RepairOrthoFrame.fromDrag(this.graphics.camera, this.rectd, candidates);
    if (!frame) {
      return false;
    }

    // outline projected onto the true element surface plane, matching the inserted area
    const outline = frame.getPreviewOutline(64);
    if (outline.length < 3) {
      return false;
    }

    const group = new THREE.Group();
    group.name = 'OrthoAreaPreview';

    // was: one flat fan-triangulated fill with a -8/-8 polygon depth-bias. The bias let it paint
    // over the girders and stiffeners standing inside the area (MV22 3rd deck: 34.6% of their
    // pixels tinted) and it sank behind curved plates (Side Shell SB: 48% coverage, 30.6% tint).
    // Replaced by the conformal fill + depth proxies below (98.5% coverage, 0% tint measured).
    // const fillGeom = new THREE.BufferGeometry();
    // fillGeom.setFromPoints(outline.map(p => p.clone()));
    // const fillIndices: number[] = [];
    // for (let i = 1; i < outline.length - 1; i++) {
    //   fillIndices.push(0, i, i + 1);
    // }
    // fillGeom.setIndex(fillIndices);
    // fillGeom.computeVertexNormals();
    // // group.add(new THREE.Mesh(fillGeom, GraphicsMaterials.orthoPreviewFillMaterial)); // was: default renderOrder 0
    // const fillMesh = new THREE.Mesh(fillGeom, GraphicsMaterials.orthoPreviewFillMaterial);
    // fillMesh.renderOrder = GraphicsRenderRepair.ORTHO_PREVIEW_RENDER_ORDER;
    // group.add(fillMesh);

    const prismPlanes = GraphicsRenderRepair.outlinePrismPlanes(outline, frame.n);
    const outlineBox = GraphicsRenderRepair._previewOutlineBox.makeEmpty();
    for (const p of outline) {
      const { u, v } = frame.toUV(p);
      outlineBox.expandByPoint(GraphicsRenderRepair._previewUV.set(u, v));
    }

    // conformal fill: the target plates' own geometry, clipped to the insertion prism, so it
    // follows curved plates and stays on the plates instead of floating in front of members
    for (const candidate of candidates) {
      const source = candidate.mesh as THREE.Mesh; // the SurfaceDesign of a target element
      if (!source?.geometry || !GraphicsRenderRepair.overlapsOutline(source, frame, outlineBox)) {
        continue;
      }
      const fill = new THREE.Mesh(source.geometry, this.orthoPreviewFillClone(source, prismPlanes));
      fill.name = 'OrthoAreaPreviewFill';
      GraphicsRenderRepair.placeLike(fill, source);
      fill.renderOrder = GraphicsRenderRepair.ORTHO_PREVIEW_RENDER_ORDER;
      group.add(fill);
    }

    // depth-only proxies of the other visible members inside the area: drawn after the
    // structure and before the fill, so the fill is depth-tested against the members in front
    // was: for (const el of this.orthoPreviewProxySourceElements()) {
    for (const el of this.orthoPreviewProxySourceElements(repairCandidates)) {
      const source = el?.SurfaceDesign;
      // was: if (!el || !el.realVisibility || el.TypeId === GraphicsRenderRepair.targetTypeId || !source?.geometry) {
      if (!el || !this.isAreaToolTargetShown(el) || el.TypeId === GraphicsRenderRepair.targetTypeId || !source?.geometry) {
        continue;
      }
      if (!GraphicsRenderRepair.overlapsOutline(source, frame, outlineBox)) {
        continue;
      }
      const proxy = new THREE.Mesh(source.geometry, GraphicsMaterials.orthoPreviewDepthMaterial);
      proxy.name = 'OrthoAreaPreviewDepth';
      GraphicsRenderRepair.placeLike(proxy, source);
      proxy.renderOrder = GraphicsRenderRepair.ORTHO_PREVIEW_DEPTH_RENDER_ORDER;
      group.add(proxy);
    }

    // the fill clones carry clipping planes, which three only applies with local clipping on
    // (no other material in the app sets clippingPlanes); guarded for a graphics without a renderer
    if (this.graphics.renderer) {
      this.graphics.renderer.localClippingEnabled = true;
    }

    // closed outline loop (depth-test disabled so it reads crisply on the surface)
    const lineGeom = new THREE.BufferGeometry().setFromPoints([
      ...outline.map(p => p.clone()),
      outline[0].clone(),
    ]);
    // group.add(new THREE.Line(lineGeom, GraphicsMaterials.orthoPreviewLineMaterial)); // was: default renderOrder 0
    const outlineLine = new THREE.Line(lineGeom, GraphicsMaterials.orthoPreviewLineMaterial);
    outlineLine.renderOrder = GraphicsRenderRepair.ORTHO_PREVIEW_RENDER_ORDER;
    group.add(outlineLine);
    this.orthoPreviewOutlineGeometry = lineGeom;

    this.graphics.scene.add(group);
    this.orthoPreview = group;
    this.previewFrame = frame;
    return true;
  }

  /**
   * Side planes of the insertion prism: one per outline edge, running along the projection
   * direction `n`, oriented so the outline centroid (the inside) is on the positive side,
   * which is the side three.js keeps when clipping. The outline is convex (rectangle,
   * circle, ellipse), so the intersection of these half-spaces is the prism.
   */
  private static outlinePrismPlanes(outline: THREE.Vector3[], n: THREE.Vector3): THREE.Plane[] {
    const centroid = new THREE.Vector3();
    outline.forEach(p => centroid.add(p));
    centroid.divideScalar(outline.length);

    const planes: THREE.Plane[] = [];
    for (let i = 0; i < outline.length; i++) {
      const a = outline[i];
      const b = outline[(i + 1) % outline.length];
      const plane = new THREE.Plane().setFromCoplanarPoints(b.clone().add(n), b, a);
      if (plane.distanceToPoint(centroid) < 0) {
        plane.negate();
      }
      planes.push(plane);
    }
    return planes;
  }

  /**
   * Elements that get a depth-only proxy (after the non-target / visibility / overlap filters).
   * With a focused plate the repair candidates are just that plate, so the members standing on
   * it come from its section instead; otherwise the repair candidate list is used
   * (`repairCandidates`, already built by updateOrthoPreview for this update).
   */
  // was: private orthoPreviewProxySourceElements(): WorkableElement[] {
  private orthoPreviewProxySourceElements(repairCandidates: WorkableElement[]): WorkableElement[] {
    if (this.focusedPlate != null) {
      const section: Section = this.focusedPlate.section;
      return section ? [...section.surfaceElements, ...section.linearElements] : [];
    }
    // was: return this.getRepairCandidateElements();
    return repairCandidates;
  }

  // scratch objects for the per-mouse-move overlap test (no per-call allocations)
  private static readonly _previewOutlineBox = new THREE.Box2();
  private static readonly _previewElementBox = new THREE.Box2();
  private static readonly _previewCorner = new THREE.Vector3();
  private static readonly _previewUV = new THREE.Vector2();

  /**
   * Cheap overlap test: the world bounding box of `mesh`, mapped through the frame's (u, v),
   * must intersect the (u, v) box of the outline. Runs per mouse move over every visible
   * element, so it reads `matrixWorld` as left by the last render (no parent walk).
   */
  private static overlapsOutline(mesh: THREE.Mesh, frame: RepairOrthoFrame, outlineBox: THREE.Box2): boolean {
    const geometry = mesh.geometry;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }
    const box = geometry.boundingBox;
    if (!box || box.isEmpty()) {
      return false;
    }
    // mesh.updateWorldMatrix(true, false); // was: parent walk per element per mouse move
    const uvBox = GraphicsRenderRepair._previewElementBox.makeEmpty();
    const corner = GraphicsRenderRepair._previewCorner;
    for (let i = 0; i < 8; i++) {
      corner.set(
        i & 1 ? box.max.x : box.min.x,
        i & 2 ? box.max.y : box.min.y,
        i & 4 ? box.max.z : box.min.z,
      ).applyMatrix4(mesh.matrixWorld);
      const { u, v } = frame.toUV(corner);
      uvBox.expandByPoint(GraphicsRenderRepair._previewUV.set(u, v));
    }
    return uvBox.intersectsBox(outlineBox);
  }

  /**
   * Puts a preview mesh exactly where `source` is drawn (the preview group sits at the origin)
   * and makes it non-pickable: it carries the full plate/member geometry and raycasts ignore
   * clipping planes, so it would otherwise answer hits outside the area.
   */
  private static placeLike(mesh: THREE.Mesh, source: THREE.Object3D): void {
    // source.updateWorldMatrix(true, false); // was: parent walk; matrices are current from the last render
    mesh.matrixAutoUpdate = false;
    mesh.matrix.copy(source.matrixWorld);
    mesh.matrixWorldNeedsUpdate = true;
    mesh.raycast = () => { };
  }

  /**
   * Fill material for one target plate: a clone of the shared template, clipped to the prism,
   * depth-tested against the scene and pulled 4 units in front of the plate's own bias (the
   * plate's factor is kept so the fill tracks it at grazing angles). One clone per distinct
   * (factor, units), kept for the whole drag so a mouse move does not recompile the shader
   * (only its clipping planes are swapped); freed by disposeOrthoPreview(true) at drag end/reset.
   */
  private orthoPreviewFillClone(source: THREE.Mesh, prismPlanes: THREE.Plane[]): THREE.Material {
    const sourceMaterial = (Array.isArray(source.material) ? source.material[0] : source.material) as THREE.Material;
    const biased = !!sourceMaterial?.polygonOffset;
    const factor = biased ? sourceMaterial.polygonOffsetFactor : 0;
    const units = (biased ? sourceMaterial.polygonOffsetUnits : 0) + GraphicsRenderRepair.ORTHO_PREVIEW_FILL_UNITS_BIAS;
    const key = `${factor}|${units}`;
    let clone = this.orthoPreviewFillClones.get(key);
    if (clone) {
      clone.clippingPlanes = prismPlanes;
    } else {
      clone = GraphicsMaterials.orthoPreviewFillMaterial.clone();
      clone.clippingPlanes = prismPlanes;
      clone.depthTest = true;
      clone.depthWrite = false;
      clone.polygonOffset = true;
      clone.polygonOffsetFactor = factor;
      clone.polygonOffsetUnits = units;
      this.orthoPreviewFillClones.set(key, clone);
    }
    return clone;
  }

  /**
   * Removes the projected preview and frees the outline geometry. The fill clones are freed only
   * when `freeMaterials` is true (drag end / reset): the per-move rebuild keeps them, because a
   * dispose releases their WebGL program and the next move would recompile the fill shader.
   * The conformal fill and the depth proxies SHARE the plates' and members' BufferGeometry, so
   * their geometry is never disposed (it would destroy the structure's own meshes); the shared
   * template / depth / line materials are kept too.
   */
  private disposeOrthoPreview(freeMaterials = false): void {
    this.previewFrame = null;
    if (freeMaterials) {
      this.orthoPreviewFillClones.forEach(clone => clone.dispose());
      this.orthoPreviewFillClones.clear();
    }
    if (this.orthoPreviewOutlineGeometry) {
      this.orthoPreviewOutlineGeometry.dispose();
      this.orthoPreviewOutlineGeometry = null;
    }
    if (!this.orthoPreview) {
      return;
    }
    this.graphics.scene.remove(this.orthoPreview);
    // was: disposed every child's geometry; the fill/proxy meshes now share the structure's
    // geometry, so only the outline geometry (above) is the preview's own.
    // this.orthoPreview.traverse((child: any) => {
    //   if (child.geometry) {
    //     child.geometry.dispose();
    //   }
    // });
    this.orthoPreview = null;
  }

  private initRoundedCorners(
    roundedCorners: RepairRoundedCorners,
    rectangleCorners: Vector3[],
  ): void {
    let calculatedRadii: number[] | number = this.rectd.cornerRadius;

    if (this.rectd.cornerRadius && rectangleCorners.length === 4) {
      calculatedRadii = [];
      for (let i = 0; i < 4; i++) {
        const c = rectangleCorners[i];
        const c_prev = rectangleCorners[(i + 3) % 4];
        const c_next = rectangleCorners[(i + 1) % 4];

        const d_prev = c.distanceTo(c_prev);
        const d_next = c.distanceTo(c_next);

        const maxR = Math.min(d_prev, d_next) / 2;

        let effR = this.rectd.cornerRadius;
        if (effR > maxR) {
          effR = maxR * 0.99;
        }
        calculatedRadii.push(effR);
      }
    }

    roundedCorners.init(rectangleCorners, this.sizeScalar, undefined, calculatedRadii);
  }


  public sizeScalar: number;
  public rectd = GraphicsRenderRepair._rectd // rectd is just an alias to the class member, _rectd.
  public focusedPlate = null;
  public focusedSection: Section = null;
  public indObj = 0;
  public meshIndex = 0;
  public focusedSceneSize: number;
  public targetsAux = []
  public static isSplit = true;
  public static targetTypeId: string;
  public inspectionsInsideArea: Array<GaugingElement>;

  // When true (default), the final area is generated as an orthographic projection along the
  // hit element's normal ("as seen from the front"), instead of the legacy camera frustum.
  public static useOrthoProjection = true;
  /**
   * Draw order of the projected area preview (fill + outline). The structure materials are
   * transparent (opacity 1, depthWrite on), so three.js sorts the plates and the preview
   * together; the preview mesh sits at the origin (world-space vertices), so plates sorted
   * nearer were drawn after it and painted over the fill, hiding the area plate by plate.
   * Structure render orders are at most 2 (SurfaceTypesMesh 1, lightsection overlays 2,
   * SECTIONS -distance); the dimension rulers stay on top at 999.
   */
  private static readonly ORTHO_PREVIEW_RENDER_ORDER = 10;
  /**
   * Draw order of the depth-only proxies of the non-target members inside the area: after the
   * structure (renderOrder <= 2) and before the fill (ORTHO_PREVIEW_RENDER_ORDER), so the fill
   * is depth-tested against the members in front of the target plates.
   */
  private static readonly ORTHO_PREVIEW_DEPTH_RENDER_ORDER = 9;
  // polygon-offset units the fill is pulled in front of its plate (added to the plate's own units)
  private static readonly ORTHO_PREVIEW_FILL_UNITS_BIAS = -4;
  private orthoFrame: RepairOrthoFrame | null = null;
  private orthoFrameAttempted = false;
  // Live world-space preview of the projected area (matches what is inserted on drop).
  private orthoPreview: THREE.Object3D | null = null;
  // The frame the live preview was built from, reused to align the dimension rulers to it.
  private previewFrame: RepairOrthoFrame | null = null;
  // Fill material clones for the current drag, keyed by "factor|units" (freed by disposeOrthoPreview(true)).
  private orthoPreviewFillClones = new Map<string, THREE.Material>();
  // The outline line's own geometry (the fill/proxy meshes share the structure's geometry).
  private orthoPreviewOutlineGeometry: THREE.BufferGeometry | null = null;

  public threeStartGeometryCloning() {
    this.graphics.isDragMode = false;
    if (!this.rectd.startRectangleSelection && !this.graphics.Controls.enableRotate) {
      this.rectd.startRectangleSelection = true
      this.graphics.Controls.enabled = false
    }
  }

  public setFocusedSection(section: Section): void {
    this.focusedSection = section;
  }

  public updateMousePosition(event, asset: Asset, showAreaDimensionRulers: boolean = false) { //chamado toda vez que o mouse se movimenta
    event = event.changedTouches ? event.changedTouches[0] : event
    if (Util.isNullOrUndefined(event.clientX)) {
      event.clientX = event.center.x;
    }
    if (Util.isNullOrUndefined(event.clientY)) {
      event.clientY = event.center.y;
    }
    this.rectdDrawCloningGeometry(event.clientX - this.graphics.offsetX, event.clientY - this.graphics.offsetY, event.buttons, asset, showAreaDimensionRulers)
  }

  public setRepairMode(argument: string) {
    this.focusedPlate = null;
    GraphicsRenderRepair.isSplit = argument === "split" ? true : false
  }

  public setElementType(typeId: string): void {
    GraphicsRenderRepair.targetTypeId = typeId;
  }

  public setShapeType(shapeType: 'rectangle' | 'circle' | 'ellipse'): void {
    this.rectd.shapeType = shapeType;
  }

  public forceRectangleShape(): void {
    this.rectd.shapeType = 'rectangle';
    this.rectd.cornerRadius = INITIAL_RECTD_STATE.cornerRadius ?? 0;
  }

  /*
   *  @brief: este método cria o quadrado de caixa de seleção para
   *          delimitar o reparo que queremos fazer. Ele tem 4 etapas (ifs),
   *          a primeira delimita quando começamos e as 3 ultimas são a
   *          seleção de fato, com o startdrag, stilldrag e enddrag
   */
  public rectdDrawCloningGeometry(mouseX, mouseY, buttons, asset: Asset, showAreaDimensionRulers: boolean = false) {
    this.asset = asset;
    if (!showAreaDimensionRulers) {
      this.graphicsRenderRuler.clearAreaDimensionRulers();
    }
    if (this.rectd.startRectangleSelection) {
      if (buttons == 1) {
        this.rectd.startRectangleSelection = false
        this.rectd.init = true
      }
      if (asset.currentSelectedElement != null && asset.currentSelectedElement instanceof StructuralElement) {
        this.focusedPlate = asset.currentSelectedElement;
        // When element is selected, also set focusedSection from the element's section
        this.focusedSection = asset.currentSelectedElement.section;
      }
      else {
        this.focusedPlate = null;
        // Find the focused section from visible regions
        // Only set if focusedSection wasn't already set externally
        if (!this.focusedSection) {
          const visibleRegions = asset.GetVisibleRegionsAllTypes();
          if (visibleRegions) {
            // First, try to find a section with selected = true
            let foundSelectedSection: Section = null;
            // Also collect all visible sections to check if only one is visible
            const visibleSections: Section[] = [];

            for (const region of visibleRegions) {
              for (const section of region.sections) {
                if (section.visible) {
                  visibleSections.push(section);
                  if (section.selected === true && !foundSelectedSection) {
                    foundSelectedSection = section;
                  }
                }
              }
            }

            // If a section is explicitly selected, use it
            if (foundSelectedSection) {
              this.focusedSection = foundSelectedSection;
            }
            // If only one section is visible (e.g., after hiding others), use that one
            else if (visibleSections.length === 1) {
              this.focusedSection = visibleSections[0];
            }
            // Otherwise, leave focusedSection null to use all visible elements
          }
        }
      }
    }

    if (!this.rectd.buttonDown && buttons == 1) {
      // start dragging

      this.rectd.buttonDown = true
      if (this.rectd.init) {
        this.graphics.isDrawingMode = true;
        this.resetRectdGeometryData();
        this.graphics.scene.add(this.rectd.decalGroup)
        this.graphics.requestRender();
        this.graphics.isDragMode = true;
      }
    } else if (this.rectd.buttonDown && buttons == 1 && this.rectd.init) {
      // still dragging — update rectangle position only

      // Create reusable drag rectangle on first still-dragging call
      if (!this._dragRectGeometry) {
        this._dragRectPositions.fill(0);
        this._dragRectGeometry = new THREE.BufferGeometry();
        const posAttr = new THREE.BufferAttribute(this._dragRectPositions, 3);
        posAttr.setUsage(THREE.DynamicDrawUsage);
        this._dragRectGeometry.setAttribute('position', posAttr);
        this._dragRectGeometry.setIndex([0, 1, 2, 0, 2, 3]);
        this._dragRectMesh = new THREE.Mesh(this._dragRectGeometry, GraphicsMaterials.selectionBoxMaterial);
        this._dragRectMesh.frustumCulled = false;
        this.graphics.camera.add(this._dragRectMesh);
        this.rectd.mesh = this._dragRectMesh;
      }

      const distz = 1 // distance from the camera

      if (this.rectd.shapeType === 'circle') {
        // Circle — per-frame Geometry recreation (complex shape)
        if (this.rectd.mesh != undefined) {
          if (this.rectd.mesh.parent) this.rectd.mesh.parent.remove(this.rectd.mesh);
          if (this.rectd.mesh.geometry) this.rectd.mesh.geometry.dispose();
        }
        this.distancePoints = []
        this.rectd.geom = new THREE.Geometry()
        this.rectd.geom1 = new THREE.Geometry()
        this.rectd.geom2 = new THREE.Geometry()
        this.rectd.geom3 = new THREE.Geometry()
        this.rectd.geom4 = new THREE.Geometry()

        this.rectd.centerX = this.rectd.origx
        this.rectd.centerY = this.rectd.origy
        const dx = mouseX - this.rectd.origx
        const dy = mouseY - this.rectd.origy
        this.rectd.radius = Math.sqrt(dx * dx + dy * dy)

        const numSegments = 64
        const circleVertices = RepairGeometryUtils.generateCircleVertices(this.graphics.camera,
          this.rectd.centerX, this.rectd.centerY, this.rectd.radius, numSegments, distz)

        for (const vertex of circleVertices) {
          this.rectd.geom.vertices.push(vertex)
        }
        const centerIndex = 0
        for (let i = 1; i < circleVertices.length; i++) {
          const nextIndex = (i + 1) % circleVertices.length
          if (nextIndex !== 0) {
            this.rectd.geom.faces.push(new THREE.Face3(centerIndex, i, nextIndex))
          }
        }

        this.rectd.inix = this.rectd.centerX - this.rectd.radius
        this.rectd.endx = this.rectd.centerX + this.rectd.radius
        this.rectd.iniy = this.rectd.centerY - this.rectd.radius
        this.rectd.endy = this.rectd.centerY + this.rectd.radius

        this.rectd.geom1.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, -100, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, this.rectd.iniy, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, this.rectd.iniy, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, -100, distz),
        )
        this.rectd.geom2.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, -100, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.inix, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.inix, -100, distz),
        )
        this.rectd.geom3.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, this.rectd.endy, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, this.rectd.endy + 2000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, this.rectd.endy + 2000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, this.rectd.endy, distz),
        )
        this.rectd.geom4.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx, -100, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, -100, distz),
        )

        const centerLocal = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.centerX, this.rectd.centerY, distz)
        const radiusLocal = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.centerX + this.rectd.radius, this.rectd.centerY, distz)
        this.distancePoints.push([centerLocal, radiusLocal])

        const rectBufferGeometry = this.rectd.geom.toBufferGeometry()
        this.rectd.mesh = new THREE.Mesh(rectBufferGeometry, GraphicsMaterials.selectionBoxMaterial)
        this.graphics.camera.add(this.rectd.mesh)

      } else if (this.rectd.shapeType === 'ellipse') {
        // Ellipse — per-frame Geometry recreation (complex shape)
        if (this.rectd.mesh != undefined) {
          if (this.rectd.mesh.parent) this.rectd.mesh.parent.remove(this.rectd.mesh);
          if (this.rectd.mesh.geometry) this.rectd.mesh.geometry.dispose();
        }
        this.distancePoints = []
        this.rectd.geom = new THREE.Geometry()
        this.rectd.geom1 = new THREE.Geometry()
        this.rectd.geom2 = new THREE.Geometry()
        this.rectd.geom3 = new THREE.Geometry()
        this.rectd.geom4 = new THREE.Geometry()

        this.rectd.centerX = this.rectd.origx
        this.rectd.centerY = this.rectd.origy
        this.rectd.radiusX = Math.abs(mouseX - this.rectd.origx)
        this.rectd.radiusY = Math.abs(mouseY - this.rectd.origy)

        const numSegments = 64
        const ellipseVertices = RepairGeometryUtils.generateEllipseVertices(this.graphics.camera,
          this.rectd.centerX, this.rectd.centerY, this.rectd.radiusX, this.rectd.radiusY, numSegments, distz)

        for (const vertex of ellipseVertices) {
          this.rectd.geom.vertices.push(vertex)
        }
        const centerIndex = 0
        for (let i = 1; i < ellipseVertices.length; i++) {
          const nextIndex = (i + 1) % ellipseVertices.length
          if (nextIndex !== 0) {
            this.rectd.geom.faces.push(new THREE.Face3(centerIndex, i, nextIndex))
          }
        }

        this.rectd.inix = this.rectd.centerX - this.rectd.radiusX
        this.rectd.endx = this.rectd.centerX + this.rectd.radiusX
        this.rectd.iniy = this.rectd.centerY - this.rectd.radiusY
        this.rectd.endy = this.rectd.centerY + this.rectd.radiusY

        this.rectd.geom1.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, -100, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, this.rectd.iniy, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, this.rectd.iniy, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, -100, distz),
        )
        this.rectd.geom2.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, -100, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.inix, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.inix, -100, distz),
        )
        this.rectd.geom3.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, this.rectd.endy, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, -500, this.rectd.endy + 2000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, this.rectd.endy + 2000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, this.rectd.endy, distz),
        )
        this.rectd.geom4.vertices.push(
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx, -100, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, 5000, distz),
          RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx + 2000, -100, distz),
        )

        const centerLocal = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.centerX, this.rectd.centerY, distz)
        const radiusXLocal = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.centerX + this.rectd.radiusX, this.rectd.centerY, distz)
        const radiusYLocal = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.centerX, this.rectd.centerY + this.rectd.radiusY, distz)
        this.distancePoints.push([centerLocal, radiusXLocal])
        this.distancePoints.push([centerLocal, radiusYLocal])

        const rectBufferGeometry = this.rectd.geom.toBufferGeometry()
        this.rectd.mesh = new THREE.Mesh(rectBufferGeometry, GraphicsMaterials.selectionBoxMaterial)
        this.graphics.camera.add(this.rectd.mesh)

      } else {
        // Rectangle — optimized with reusable BufferGeometry
        if (!this._dragRectGeometry) {
          this._dragRectPositions.fill(0);
          this._dragRectGeometry = new THREE.BufferGeometry();
          const posAttr = new THREE.BufferAttribute(this._dragRectPositions, 3);
          posAttr.setUsage(THREE.DynamicDrawUsage);
          this._dragRectGeometry.setAttribute('position', posAttr);
          this._dragRectGeometry.setIndex([0, 1, 2, 0, 2, 3]);
          this._dragRectMesh = new THREE.Mesh(this._dragRectGeometry, GraphicsMaterials.selectionBoxMaterial);
          this._dragRectMesh.frustumCulled = false;
          this.graphics.camera.add(this._dragRectMesh);
          this.rectd.mesh = this._dragRectMesh;
        }

        if (this.rectd.origx < mouseX) {
          this.rectd.inix = this.rectd.origx;
          this.rectd.endx = mouseX;
        } else {
          this.rectd.inix = mouseX;
          this.rectd.endx = this.rectd.origx;
        }

        if (this.rectd.origy < mouseY) {
          this.rectd.iniy = this.rectd.origy;
          this.rectd.endy = mouseY;
        } else {
          this.rectd.iniy = mouseY;
          this.rectd.endy = this.rectd.origy;
        }

        const p0 = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.inix, this.rectd.iniy, distz);
        const p1 = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.inix, this.rectd.endy, distz);
        const p2 = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx, this.rectd.endy, distz);
        const p3 = RepairGeometryUtils.screenToLocal(this.graphics.camera, this.rectd.endx, this.rectd.iniy, distz);

        const pos = this._dragRectPositions;
        pos[0] = p0.x; pos[1] = p0.y; pos[2] = p0.z;
        pos[3] = p1.x; pos[4] = p1.y; pos[5] = p1.z;
        pos[6] = p2.x; pos[7] = p2.y; pos[8] = p2.z;
        pos[9] = p3.x; pos[10] = p3.y; pos[11] = p3.z;

        (this._dragRectGeometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      }

      // Show the projected preview (matching the area that will be inserted) when the ortho
      // frame can be built; otherwise fall back to the flat screen-space preview above.
      if (this.updateOrthoPreview()) {
        this.rectd.mesh.visible = false;
      }

      if (showAreaDimensionRulers) {
        this.renderAreaDimensionRulers();
      }
      this.graphics.requestRender();

    } else if (this.rectd.buttonDown && buttons == 0 && this.rectd.init) {
      // end dragging
      this.graphicsRenderRuler.clearAreaDimensionRulers();

      if (this.rectd.shapeType === 'circle') {
        this.sizeScalar = this.rectd.radius || 0;
      } else {
        let deltaX = Math.abs(this.rectd.inix - this.rectd.endx)
        let deltaY = Math.abs(this.rectd.iniy - this.rectd.endy)
        this.sizeScalar = Math.min(deltaX, deltaY);
      }
      this.targetsAux = [];
      this.rectd.init = false
      this.rectd.buttonDown = false
      this.graphics.isDrawingMode = false;
      this.graphics.isDragMode = false;
      this.graphics.Controls.enabled = true

      // For rectangles, compute mask geometries + distancePoints now (deferred from drag for perf).
      // Circle/ellipse already computed these during "still dragging".
      if (this.rectd.shapeType === 'rectangle' || !this.rectd.geom1) {
        const distz = 1;
        this.rectd.geom1 = new THREE.Geometry();
        this.rectd.geom1.vertices.push(
          this.screenToLocal(-500, -100, distz),
          this.screenToLocal(-500, this.rectd.iniy, distz),
          this.screenToLocal(this.rectd.endx + 2000, this.rectd.iniy, distz),
          this.screenToLocal(this.rectd.endx + 2000, -100, distz),
        );
        this.rectd.geom2 = new THREE.Geometry();
        this.rectd.geom2.vertices.push(
          this.screenToLocal(-500, -100, distz),
          this.screenToLocal(-500, 5000, distz),
          this.screenToLocal(this.rectd.inix, 5000, distz),
          this.screenToLocal(this.rectd.inix, -100, distz),
        );
        this.rectd.geom3 = new THREE.Geometry();
        this.rectd.geom3.vertices.push(
          this.screenToLocal(-500, this.rectd.endy, distz),
          this.screenToLocal(-500, this.rectd.endy + 2000, distz),
          this.screenToLocal(this.rectd.endx + 2000, this.rectd.endy + 2000, distz),
          this.screenToLocal(this.rectd.endx + 2000, this.rectd.endy, distz),
        );
        this.rectd.geom4 = new THREE.Geometry();
        this.rectd.geom4.vertices.push(
          this.screenToLocal(this.rectd.endx, -100, distz),
          this.screenToLocal(this.rectd.endx, 5000, distz),
          this.screenToLocal(this.rectd.endx + 2000, 5000, distz),
          this.screenToLocal(this.rectd.endx + 2000, -100, distz),
        );

        this.distancePoints = [];
        this.distancePoints.push([
          this.screenToLocal(this.rectd.inix, this.rectd.iniy, distz),
          this.screenToLocal(this.rectd.inix, this.rectd.endy, distz)
        ]);
        this.distancePoints.push([
          this.screenToLocal(this.rectd.inix, this.rectd.iniy, distz),
          this.screenToLocal(this.rectd.endx, this.rectd.iniy, distz),
        ]);
      }
      this.disposeOrthoPreview(true); // drag end: also frees the drag's fill clones
      this.indObj = 0;
      this.meshIndex = 0;

      try {
        this.createDecalGeometry()
        while (this.indObj <= this.focusedSceneSize) {
          this.createDecalGeometry()
        }
      } finally {
        // Dispose the drag-shape mesh — guaranteed even if createDecalGeometry throws.
        // Rectangle path creates `_dragRectMesh` and aliases it as `rectd.mesh`;
        // circle/ellipse paths create `rectd.mesh` directly with its own BufferGeometry
        // and leave `_dragRectMesh` null, so we have to handle both refs.
        if (this._dragRectMesh) {
          this.graphics.camera.remove(this._dragRectMesh);
          this._dragRectGeometry.dispose();
          this._dragRectGeometry = null;
          this._dragRectMesh = null;
        }
        if (this.rectd.mesh) {
          if (this.rectd.mesh.parent) this.rectd.mesh.parent.remove(this.rectd.mesh);
          if (this.rectd.mesh.geometry) this.rectd.mesh.geometry.dispose();
          this.rectd.mesh = undefined;
        }
      }

      let lines = []

      for (let points of this.distancePoints) {
        let linesAux = [...points].map((v) =>
          this.graphics.camera.localToWorld(v))
        lines.push(linesAux)
      }

      this.focusedPlate = null;
      this.focusedSection = null;
      this.orthoFrame = null;
      this.orthoFrameAttempted = false;
      this.graphics.requestRender();
    } else if (this.rectd.buttonDown && buttons == 0 && !this.rectd.init) {
      // click release without drawing — reset button state
      this.rectd.buttonDown = false;
    }

  }

  private renderAreaDimensionRulers(): void {
    const targetMeshes = this.getAreaDimensionTargetMeshes();
    if (targetMeshes.length === 0) {
      this.graphicsRenderRuler.clearAreaDimensionRulers();
      return;
    }

    // When the projected preview is shown, align the rulers to it (world-aligned ortho rectangle/
    // circle/ellipse) instead of the camera-view-aligned screen bounds. Same rule as the legacy
    // path below: measure only the part of the area that actually lies on an element — the
    // ruler starts where the area touches the element, not at the drag origin.
    if (this.previewFrame) {
      const validPoints = this.graphicsRenderRuler.getValidAreaSurfacePoints(
        this.getAreaDimensionScreenBounds(), targetMeshes);
      if (validPoints.length < 2) {
        this.graphicsRenderRuler.clearAreaDimensionRulers();
        return;
      }
      this.renderAreaDimensionRulersFromFrame(this.previewFrame, validPoints);
      return;
    }

    if (this.rectd.shapeType === 'circle') {
      this.graphicsRenderRuler.updateAreaDimensionRulers([
        {
          label: 'D',
          start: new THREE.Vector2(this.rectd.inix, this.rectd.centerY),
          end: new THREE.Vector2(this.rectd.endx, this.rectd.centerY),
          useMaxLabelDistance: true,
        },
        {
          label: 'D',
          start: new THREE.Vector2(this.rectd.centerX, this.rectd.iniy),
          end: new THREE.Vector2(this.rectd.centerX, this.rectd.endy),
          showLabel: false,
          useMaxLabelDistance: true,
        },
      ], targetMeshes);
      return;
    }

    this.graphicsRenderRuler.updateAreaDimensionRulersFromValidBounds(
      this.getAreaDimensionScreenBounds(), targetMeshes);
  }

  /** Screen-space bounds of the current drag (inix/endx already normalized to min/max). */
  private getAreaDimensionScreenBounds() {
    return {
      minX: this.rectd.inix,
      maxX: this.rectd.endx,
      minY: this.rectd.iniy,
      maxY: this.rectd.endy,
      shapeType: this.rectd.shapeType,
      centerX: this.rectd.centerX,
      centerY: this.rectd.centerY,
      radius: this.rectd.radius,
      radiusX: this.rectd.radiusX,
      radiusY: this.rectd.radiusY,
      labelMode: 'widthHeight' as const,
    };
  }

  /**
   * Builds the dimension rulers from the preview ortho frame, measuring only the extent of
   * the valid (on-element) surface points clamped to the preview shape — like the legacy
   * path, the ruler spans from where the area touches the element, not the whole drag.
   */
  private renderAreaDimensionRulersFromFrame(frame: RepairOrthoFrame, validPoints: THREE.Vector3[]): void {
    let extentU: number;
    let extentV: number;
    if (frame.shapeType === 'circle') {
      extentU = extentV = frame.radius;
    } else if (frame.shapeType === 'ellipse') {
      extentU = frame.radiusU;
      extentV = frame.radiusV;
    } else {
      extentU = frame.halfWidth;
      extentV = frame.halfHeight;
    }

    // Only points on the frame element's own surface count: raycast hits on other structure
    // behind/in front (same-type elements at a different depth) must not stretch the
    // measurement past where the shape touches THIS element.
    const surfaceTolerance = Math.max(0.02, 0.05 * Math.hypot(extentU, extentV));
    const onSurfacePoints = validPoints.filter(p =>
      Math.abs(frame.surfaceNormal.dot(p.clone().sub(frame.hitPoint))) <= surfaceTolerance);
    if (onSurfacePoints.length < 2) {
      this.graphicsRenderRuler.clearAreaDimensionRulers();
      return;
    }

    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    for (const p of onSurfacePoints) {
      const { u, v } = frame.toUV(p);
      uMin = Math.min(uMin, u);
      uMax = Math.max(uMax, u);
      vMin = Math.min(vMin, v);
      vMax = Math.max(vMax, v);
    }
    const uLo = Math.max(-extentU, uMin);
    const uHi = Math.min(extentU, uMax);
    const vLo = Math.max(-extentV, vMin);
    const vHi = Math.min(extentV, vMax);
    if (uHi - uLo <= 0 && vHi - vLo <= 0) {
      this.graphicsRenderRuler.clearAreaDimensionRulers();
      return;
    }

    const segments: { label: string; start: THREE.Vector3; end: THREE.Vector3; showLabel?: boolean }[] = [];
    if (frame.shapeType === 'circle') {
      // diameters through the centre of the valid region, like the legacy circle rulers
      const uMid = (uLo + uHi) / 2;
      const vMid = (vLo + vHi) / 2;
      if (uHi - uLo > 0) {
        segments.push({ label: 'D', start: frame.surfaceUVPoint(uLo, vMid), end: frame.surfaceUVPoint(uHi, vMid) });
      }
      if (vHi - vLo > 0) {
        segments.push({ label: 'D', start: frame.surfaceUVPoint(uMid, vLo), end: frame.surfaceUVPoint(uMid, vHi), showLabel: false });
      }
    } else {
      // L-shaped W/H rulers at the valid region's corner, like the legacy rectangle rulers
      if (uHi - uLo > 0) {
        segments.push({ label: 'W', start: frame.surfaceUVPoint(uLo, vLo), end: frame.surfaceUVPoint(uHi, vLo) });
      }
      if (vHi - vLo > 0) {
        segments.push({ label: 'H', start: frame.surfaceUVPoint(uLo, vLo), end: frame.surfaceUVPoint(uLo, vHi) });
      }
    }

    const center = frame.surfaceUVPoint((uLo + uHi) / 2, (vLo + vHi) / 2);
    this.graphicsRenderRuler.updateAreaDimensionRulersFromWorldSegments(segments, center);
  }

  private getAreaDimensionTargetMeshes(): THREE.Object3D[] {
    let elements: WorkableElement[];
    if (this.focusedPlate) {
      elements = [this.focusedPlate];
    } else if (this.focusedSection) {
      elements = [...this.focusedSection.surfaceElements, ...this.focusedSection.linearElements];
    } else {
      // User rule: in new-format REGIONS nothing is measured until a section (or plate) is
      // selected, matching getRepairCandidateElements (no area, no projection there).
      // was: elements = this.asset?.getStructureAndRepairsElements(true) ?? [];
      // was: elements = this.asset?.getStructureAndRepairsElements(!this.isNewModelRegions) ?? [];
      elements = this.isNewModelRegions ? [] : this.asset?.getStructureAndRepairsElements(true) ?? [];
    }

    return elements
      // was: .filter(element => element.realVisibility && element.TypeId === GraphicsRenderRepair.targetTypeId)
      // was: .filter(element => this.isAreaDimensionTargetShown(element) && element.TypeId === GraphicsRenderRepair.targetTypeId)
      .filter(element => this.isAreaToolTargetShown(element) && element.TypeId === GraphicsRenderRepair.targetTypeId)
      .map(element => element.SurfaceDesign);
  }

  /**
   * Whether an element is on screen for the area tool: the dimension ruler, the projected
   * preview (frame candidates), its depth proxies and the insertion ortho frame.
   * New-format REGIONS hides each section's `surfaceGroup`/`linearGroup` and draws the merged
   * lightsection instead, so `realVisibility` is false for the elements the user sees. There an
   * element counts as shown when its own mesh and its section (which already covers the region)
   * are visible. Like `getRepairCandidateElements` and the `isNewModelRegions` decal checks, this
   * does not trust `realVisibility` in new-format REGIONS, but it still requires the element's
   * own mesh and its section to be visible. Elsewhere `realVisibility` applies.
   */
  // was: private isAreaDimensionTargetShown(element: WorkableElement): boolean {
  private isAreaToolTargetShown(element: WorkableElement): boolean {
    if (this.isNewModelRegions) {
      return !!element.SurfaceDesign?.visible && !!element.section?.visible;
    }
    return element.realVisibility;
  }

  /** New-format asset shown in REGIONS mode (lightsections drawn, section surface groups hidden). */
  private get isNewModelRegions(): boolean {
    return !!(this.asset?.isNewModelMode && this.asset?.mode === 'REGIONS');
  }

  public createDecalGeometry() {
    this.rectd.meshsAfterCut = [];
    // Define frustum's 4 planes, detect sectioned triangles and create their corresponding new vertices.
    //    added feature: rounded corners.
    //
    // NOTE 1: 'plane' will be used in this function comments to mean 'frustum plane'
    // NOTE 2: to increase processing speed, it may (i don't know) help if all coordinates were transformed from
    //       frustum-based (perspective) to cube-based (orthogonal), do all calculations in this simpler coordinate space, and
    //       when the final triangles were generated, convert them back to frustum coords.
    // NOTE 3 - (JUL-06-2020) - this algorithm projects a rectangle onto the plate's surface, meaning if the surface is
    //                          *tilted*, then the projected rectangle will generate a *trapezoid* geometry on the surface.
    //                          (if what's really wanted is a rectangle on a tilted surface, then the algorithm must be changed
    //                           to draw the rectangle following the surface geometry in 'real-time'. in this case (tilted surface),
    //                           this 'rectangle' would loose its right angles, gradually becoming a 'trapezoid' as the mouse drags
    //                           over the underlying geometry)

    // detect side of geometry (-1, 1)
    let sideSign = 0

    let planes

    const clipper = new RepairGeometryClipper(this.graphics.camera);

    // Build the orthographic projection frame once per drag. If it cannot be built
    // (shape center misses geometry, grazing view, ...), fall back to the legacy
    // camera-frustum algorithm for this drag.
    if (GraphicsRenderRepair.useOrthoProjection && !this.orthoFrameAttempted) {
      this.orthoFrameAttempted = true;
      // filter moved to orthoFrameCandidates() with realVisibility -> isAreaToolTargetShown; base was:
      // was:   .filter(el => el && el.realVisibility && el.TypeId === GraphicsRenderRepair.targetTypeId && el.SurfaceDesign)
      const candidates = this.orthoFrameCandidates(this.getRepairCandidateElements());
      this.orthoFrame = RepairOrthoFrame.fromDrag(this.graphics.camera, this.rectd, candidates);
      if (this.orthoFrame) {
        // rounded-corner radius limits are world units in ortho mode (legacy keeps pixels)
        if (this.orthoFrame.shapeType === 'circle') {
          this.sizeScalar = this.orthoFrame.radius;
        } else if (this.orthoFrame.shapeType === 'ellipse') {
          this.sizeScalar = Math.min(2 * this.orthoFrame.radiusU, 2 * this.orthoFrame.radiusV);
        } else {
          this.sizeScalar = Math.min(2 * this.orthoFrame.halfWidth, 2 * this.orthoFrame.halfHeight);
        }
      }
    }

    if (this.orthoFrame) {
      clipper.initOrtho(this.orthoFrame);
      planes = clipper.getPlanes();
    } else {
      // After adding type to mesh, the previous code gave an error. Fixing it for either Geometry or BufferGeometry
      // could have unintended side effects. Therefore, we will keep this weird check for now.
      let vertices: THREE.Vector3[] = [];

      if (this.rectd.mesh.geometry instanceof THREE.BufferGeometry) {
        const positions = this.rectd.mesh.geometry.attributes.position;
        const uniquePoints = new Map<string, THREE.Vector3>();

        for (let i = 0; i < positions.count; i++) {
          const point = new THREE.Vector3().fromBufferAttribute(positions, i);
          const key = `${point.x.toFixed(6)}_${point.y.toFixed(6)}_${point.z.toFixed(6)}`;
          if (!uniquePoints.has(key)) {
            uniquePoints.set(key, point);
          }
        }

        vertices = Array.from(uniquePoints.values()).map((point) =>
          this.graphics.camera.localToWorld(point.clone())
        );

        if (vertices.length > 4) {
          vertices = vertices.slice(0, 4);
        }
      } else if ('vertices' in this.rectd.mesh.geometry) {
        vertices = [...(this.rectd.mesh.geometry as any).vertices].map((v) =>
          this.graphics.camera.localToWorld(v.clone())
        )
      }

      if (this.rectd.shapeType === 'circle') {
        if (this.rectd.centerX !== undefined && this.rectd.centerY !== undefined && this.rectd.radius !== undefined) {
          clipper.initFromCircle(this.rectd.centerX, this.rectd.centerY, this.rectd.radius);
          // Keep local vars for now if needed by downstream code not yet refactored (though mostly used in loop which we will refactor next)
        } else {
          clipper.initFromVerticesForCircle(vertices);
        }
      } else if (this.rectd.shapeType === 'ellipse') {
        if (this.rectd.centerX !== undefined &&
          this.rectd.centerY !== undefined &&
          this.rectd.radiusX !== undefined &&
          this.rectd.radiusY !== undefined) {
          clipper.initFromEllipse(
            this.rectd.centerX,
            this.rectd.centerY,
            this.rectd.radiusX,
            this.rectd.radiusY
          );
        }
      } else {
        clipper.initFromRectangle(vertices);
      }
    }
    planes = clipper.getPlanes();



    // let pl1 = []

    // if (planes[0].constant > planes[2].constant) {
    //   let npl = new THREE.Plane(planes[2].normal.clone().multiplyScalar(-1), planes[0].constant + 200);
    //   let npRight = new THREE.Plane(planes[0].normal.clone().multiplyScalar(-1), planes[0].constant);
    //   let nptop = new THREE.Plane(planes[3].normal.clone(), planes[3].constant - 200);
    //   let npbot = new THREE.Plane(planes[1].normal.clone(), planes[1].constant + 200);

    //   pl1.push(npl)
    //   pl1.push(npRight)
    //   pl1.push(nptop)
    //   pl1.push(npbot)

    //   planes = pl1;
    // }

    // pl1.push(new THREE.Plane(planes[0].normal.clone(), (planes[0].constant - planes[1].constant) * ));

    let listCornersInPlate = [0, 0, 0, 0]

    //-----------------------------------------------------------------
    // 'listPlateArcsForAllPlates' will be filled with 4 sub-lists, one for each rectangle's corner
    //    each sub-list contains interpolated vertices making up a quarter of a circle for the corresponding rectangle's corner.
    //
    let listPlateArcsForAllPlates = []

    // indicates where's the front face (to be used in the function 'createPartialTriangles')
    let sectNormalAllPlates = new THREE.Vector3()

    let listPos = []
    let listPosNormals = [] // same as listPos, but with a tiny offset in the 'sectorNormal' direction

    // this list is the same as 'listPos' but it is divided in sublists ('listTrisInPlate') one for each plate.
    let listPosPerPlate = []
    let listPosPerPlateNormals = []

    let targets = [];



    // let allPlates = this.graphicsNavigatorService.graphics.sectionNavigator
    //   .filter((sec) => sec.id == this.graphicsNavigatorService.graphics.currentSection.id)
    //   .flatMap((sec) => sec.regions)
    //   .flatMap((x) => x.plates)

    let allInpectionElements: Array<WorkableElement> = this.asset?.getInspectionElements(true);
    // Se focusedPlate for null, todos os objetos são selecionados, se não for, somente o selecionado pode ser reparado
    // If focusedSection is set, only elements from that section are selected
    let allPlates: WorkableElement[] = this.getRepairCandidateElements();
    this.sceneObjs = allPlates;

    this.focusedSceneSize = this.sceneObjs.length;
    // let secNav = this.graphicsNavigatorService.graphics.sectionNavigator
    // let curId = this.graphicsNavigatorService.graphics.currentSection.id
    this.inspectionsInsideArea = [];
    this.rectd.targets = []
    for (; this.indObj <= this.sceneObjs.length; this.indObj++) {
      //for(let obj of sceneObjs){
      //let ind:number =  Number.parseInt(indObj);
      let workableElement: WorkableElement = this.sceneObjs[this.indObj] // 'ind' will be necessary further down in the code (hence using 'in' instead of 'of' in the for loop above)

      if (Util.isNullOrUndefined(workableElement)) {
        continue
      }
      this.processRepairObject(clipper, workableElement, planes, targets, listPos, listPosPerPlate, listPosNormals, listPosPerPlateNormals, listCornersInPlate, listPlateArcsForAllPlates, sectNormalAllPlates); // if visible && "plate"
    }
    // if(this.asset.)
    this.checkInspectionsInsideArea(allInpectionElements, planes);

    if (!GraphicsRenderRepair.isSplit) {
      // Ensure listPos is initialized for current meshIndex before incrementing
      if (Util.isNullOrUndefined(listPos[this.meshIndex])) {
        listPos[this.meshIndex] = []
      }
      this.meshIndex++;
      this.finalizeRepair(listCornersInPlate, listPlateArcsForAllPlates, sectNormalAllPlates, sideSign, listPos, listPosPerPlate, listPosNormals, listPosPerPlateNormals
        , targets)
      if (this.rectd.isCut) {
        let index = 0;
        for (const target of targets) {
          this.rectd.meshsAfterCut[index] = [];
          const t = this.asset.getStructureAndRepairsElements(true).find(x => x.Guid === target.name);
          if (this.orthoFrame && t) {
            // complement rectangles built around the rectified shape in the surface plane
            const extent = this.computeOrthoComplementExtent(t);
            const complementQuads = this.orthoFrame.buildComplementRectCorners(extent);
            for (const quad of complementQuads) {
              this.createDecalGeometryOriginalPlate(quad, t, index)
            }
          } else {
            this.createDecalGeometryOriginalPlate(this.rectd.geom1.clone(), t, index)
            this.createDecalGeometryOriginalPlate(this.rectd.geom2.clone(), t, index)
            this.createDecalGeometryOriginalPlate(this.rectd.geom3.clone(), t, index)
            this.createDecalGeometryOriginalPlate(this.rectd.geom4.clone(), t, index)
          }
          index++;
        }
      }
    }
  }

  /**
   * Half-size, in frame (u, v) units, that the cut-mode complement rectangles must span
   * to cover the whole target plate (analog of the legacy +2000/5000 px screen slop).
   */
  private computeOrthoComplementExtent(element: WorkableElement): number {
    const box = new THREE.Box3().setFromObject(element.SurfaceDesign);
    let maxUV = 0;
    for (let i = 0; i < 8; i++) {
      const corner = new THREE.Vector3(
        i & 1 ? box.max.x : box.min.x,
        i & 2 ? box.max.y : box.min.y,
        i & 4 ? box.max.z : box.min.z,
      );
      const uv = this.orthoFrame.toUV(corner);
      maxUV = Math.max(maxUV, Math.abs(uv.u), Math.abs(uv.v));
    }
    const f = this.orthoFrame;
    const holeMax = Math.max(f.halfWidth, f.halfHeight, f.radius, f.radiusU, f.radiusV);
    return Math.max(maxUV, holeMax * 2) * 1.1;
  }

  public createDecalGeometryOriginalPlate(geometry, target, index) {
    // Define frustum's 4 planes, detect sectioned triangles and create their corresponding new vertices.
    //    added feature: rounded corners.
    //
    // NOTE 1: 'plane' will be used in this function comments to mean 'frustum plane'
    // NOTE 2: to increase processing speed, it may (i don't know) help if all coordinates were transformed from
    //       frustum-based (perspective) to cube-based (orthogonal), do all calculations in this simpler coordinate space, and
    //       when the final triangles were generated, convert them back to frustum coords.
    // NOTE 3 - (JUL-06-2020) - this algorithm projects a rectangle onto the plate's surface, meaning if the surface is
    //                          *tilted*, then the projected rectangle will generate a *trapezoid* geometry on the surface.
    //                          (if what's really wanted is a rectangle on a tilted surface, then the algorithm must be changed
    //                           to draw the rectangle following the surface geometry in 'real-time'. in this case (tilted surface),
    //                           this 'rectangle' would loose its right angles, gradually becoming a 'trapezoid' as the mouse drags
    //                           over the underlying geometry)

    // detect side of geometry (-1, 1)
    let sideSign = 0

    let planes
    const clipper = new RepairGeometryClipper(this.graphics.camera);
    {
      if (Array.isArray(geometry)) {
        // ortho mode: geometry is a world-space corner quad on the surface plane
        clipper.initFromOrthoRectCorners(geometry, this.orthoFrame.n);
      } else {
        const rect = [...geometry.vertices].map((v) =>
          this.graphics.camera.localToWorld(v),
        )
        clipper.initFromRectangle(rect);
      }
      planes = clipper.getPlanes();
    }



    // let pl1 = []

    // if (planes[0].constant > planes[2].constant) {
    //   let npl = new THREE.Plane(planes[2].normal.clone().multiplyScalar(-1), planes[0].constant + 200);
    //   let npRight = new THREE.Plane(planes[0].normal.clone().multiplyScalar(-1), planes[0].constant);
    //   let nptop = new THREE.Plane(planes[3].normal.clone(), planes[3].constant - 200);
    //   let npbot = new THREE.Plane(planes[1].normal.clone(), planes[1].constant + 200);

    //   pl1.push(npl)
    //   pl1.push(npRight)
    //   pl1.push(nptop)
    //   pl1.push(npbot)

    //   planes = pl1;
    // }

    // pl1.push(new THREE.Plane(planes[0].normal.clone(), (planes[0].constant - planes[1].constant) * ));

    let listCornersInPlate = [0, 0, 0, 0]

    //-----------------------------------------------------------------
    // 'listPlateArcsForAllPlates' will be filled with 4 sub-lists, one for each rectangle's corner
    //    each sub-list contains interpolated vertices making up a quarter of a circle for the corresponding rectangle's corner.
    //
    let listPlateArcsForAllPlates = []

    // indicates where's the front face (to be used in the function 'createPartialTriangles')
    let sectNormalAllPlates = new THREE.Vector3()

    let listPos = []
    let listPosNormals = [] // same as listPos, but with a tiny offset in the 'sectorNormal' direction

    // this list is the same as 'listPos' but it is divided in sublists ('listTrisInPlate') one for each plate.
    let listPosPerPlate = []
    let listPosPerPlateNormals = []

    let targets = [];

    let tri = new THREE.Triangle()
    let _verts = [tri.a, tri.b, tri.c]

    let fline = (p1, p2) => new THREE.Line3(p1, p2)
    let edges = [fline(tri.a, tri.b), fline(tri.b, tri.c), fline(tri.c, tri.a)]
    let listAdjacentPlanes = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
    ] // Note: this list's indexes can't be used in 'planes' as they are used for different planes, even if their indexes are the same.
    let tmpPlane = new THREE.Plane()
    let funcAddCorners = (listTarget) =>
      listAdjacentPlanes.forEach((planePair) =>
        listTarget.push(
          RepairGeometryUtils.vertIntersectPlanes(
            tri.getPlane(tmpPlane),
            planes[planePair[0]],
            planes[planePair[1]],
          ),
        ),
      )
    let funcAddCornersFromPlate = (listTarget, platePlane) =>
      listAdjacentPlanes.forEach((planePair) =>
        listTarget.push(
          RepairGeometryUtils.vertIntersectPlanes(platePlane, planes[planePair[0]], planes[planePair[1]]),
        ),
      )
    let listAllOtherPlanes = [
      [1, 2, 3],
      [0, 2, 3],
      [0, 1, 3],
      [0, 1, 2],
    ]

    // let allPlates = this.graphicsNavigatorService.graphics.sectionNavigator
    //   .filter((sec) => sec.id == this.graphicsNavigatorService.graphics.currentSection.id)
    //   .flatMap((sec) => sec.regions)
    //   .flatMap((x) => x.plates)

    // let allPlates = this.asset.getStructureElements(true);
    // let allInpectionElements: Array<WorkableElement> = this.asset?.getInspectionElements(true);
    // Se focusedPlate for null, todos os objetos são selecionados, se não for, somente o selecionado pode ser reparado
    const _targets = [target];//this.focusedPlate == null ? allPlates : [this.focusedPlate]

    this.focusedSceneSize = this.sceneObjs.length;
    // let secNav = this.graphicsNavigatorService.graphics.sectionNavigator
    // let curId = this.graphicsNavigatorService.graphics.currentSection.id
    this.inspectionsInsideArea = [];

    for (let i = 0; i <= _targets.length; i++) {
      //for(let obj of sceneObjs){
      //let ind:number =  Number.parseInt(indObj);
      let workableElement: WorkableElement = _targets[i] // 'ind' will be necessary further down in the code (hence using 'in' instead of 'of' in the for loop above)

      if (Util.isNullOrUndefined(workableElement)) {
        continue
      }

      // was: const isNewModelRegions = this.asset?.isNewModelMode && this.asset?.mode === 'REGIONS';
      // was: if (isNewModelRegions || workableElement.realVisibility) {
      if (this.isNewModelRegions || workableElement.realVisibility) {
        if (workableElement.TypeId !== GraphicsRenderRepair.targetTypeId) {
          continue;
        }

        // if (workableElement.Guid !== '0e6336c134354e7a8bf01d0d58846eb8')
        //   continue
        const obj = workableElement.SurfaceDesign as any;
        // if (Util.isNullOrUndefined(workableElement.elementNormal)) {
        //   const _normals = obj.geometry.attributes.normal;
        //   const indexes = obj.geometry.index;
        //   obj.userData.ElementNormal = new THREE.Vector3();
        //   for (const _index of indexes) {
        //     obj.userData.ElementNormal.add(new THREE.Vector3(_normals[_index * 3], _normals[_index * 3 + 1], _normals[_index * 3 + 2]));
        //   }
        //   obj.userData.ElementNormal.normalize();
        // }

        // obj.userData.ElementNormal = new THREE.Vector3(obj.userData.ElementNormal.x, obj.userData.ElementNormal.y, obj.userData.ElementNormal.z).normalize();

        let sectNormal = workableElement.elementNormal ? workableElement.elementNormal : workableElement.section.sectionNormal;
        let aPos = obj.geometry.attributes.position.array
        let aInd = obj.geometry.index

        let geoNormal = obj.userData.GeoNormal
        let pos = obj.position

        //--------------------------------------------------------------------

        let offsetPos = false;//pos.x != 0 || pos.y != 0 || pos.z != 0

        let aVec = []

        if (aInd != null) {
          for (let i of aInd.array) {
            let x = aPos[i * 3],
              y = aPos[i * 3 + 1],
              z = aPos[i * 3 + 2]
            aVec.push(offsetPos ? new THREE.Vector3(z, y, x).add(pos) : new THREE.Vector3(x, y, z))
          }
        } else {
          for (let i = 0; i < aPos.length; i += 3) {
            let x = aPos[i],
              y = aPos[i + 1],
              z = aPos[i + 2]
            aVec.push(offsetPos ? new THREE.Vector3(z, y, x).add(pos) : new THREE.Vector3(x, y, z))
          }
        }

        let listTrisInPlate = [] // this sublist will be added to 'listPosPerPlate'
        let listTrisInPlateNormals = []

        sectNormalAllPlates = new THREE.Vector3(sectNormal.x, sectNormal.y, sectNormal.z);

        // get the rectangle's 4 corners coordinates
        let rectangleCorners: Vector3[] = []

        // _verts.forEach((v, ind) => v.fromArray(aPos, ind * 3)) // fills 'tri' just to get the plane below (tri.getPlane())
        _verts.forEach((v, ind) => v.copy(aVec[ind])) // fills 'tri' just to get the plane below (tri.getPlane())
        let tmpPlane = new THREE.Plane()

        let counter = 1;

        // TODO: mais pra frente, consertar esse algoritmo de 'cantos arredondados' usando a var 'listPlateArcsForAllPlates', etc, pois o codigo atual esta redundante.
        //       por ex: na linha abaixo, esta considerando todos os 4 cantos no mesmo plano, o que eh falso se o retangulo englobar mais de 2 planos
        let testPlane = tri.getPlane(tmpPlane);
        testPlane.setFromCoplanarPoints(tri.a, tri.b, tri.c)

        const canonicalDirection = Util.LargestCanonicalAxisDirection(workableElement.section.sectionNormal)
        while ((testPlane.constant === 0 || Object.is(testPlane.constant, -0)) && testPlane.normal.equals(new THREE.Vector3()) && counter < aVec.length / 3) {
          _verts[0] = aVec[counter * 3];
          _verts[1] = aVec[counter * 3 + 1];
          _verts[2] = aVec[counter * 3 + 2];
          testPlane = tri.getPlane(tmpPlane);

          if (canonicalDirection === 'x')
            testPlane.constant = testPlane.constant * -1;
          counter++;
        }
        let centerBbox = new THREE.Vector3();
        if ((testPlane.constant === 0 || Object.is(testPlane.constant, -0)) && testPlane.normal.equals(new THREE.Vector3())) {
          workableElement.SurfaceDesign.geometry.boundingBox.getCenter(centerBbox)
          if (canonicalDirection === 'x')
            testPlane = new THREE.Plane(workableElement.section.sectionNormal, centerBbox.x)
          else if (canonicalDirection === 'y')
            testPlane = new THREE.Plane(workableElement.section.sectionNormal, centerBbox.y)
          else if (canonicalDirection === 'z')
            testPlane = new THREE.Plane(workableElement.section.sectionNormal, centerBbox.z)
          if (canonicalDirection === 'x')
            testPlane.constant = testPlane.constant * -1;
          // testPlane.constant = testPlane.constant * -1;
        }
        // with prism planes (ortho mode), corner intersection degenerates when the surface
        // plane contains the projection direction (element edge-on to the prism)
        const cornersDegenerate = (plane: THREE.Plane) =>
          this.orthoFrame != null && Math.abs(plane.normal.dot(this.orthoFrame.n)) < 0.1;

        // funcAddCornersFromPlate(rectangleCorners, tri.getPlane(tmpPlane))
        if (!cornersDegenerate(testPlane)) {
          funcAddCornersFromPlate(rectangleCorners, testPlane)
        }

        const roundedCorners = new RepairRoundedCorners();
        this.initRoundedCorners(roundedCorners, rectangleCorners);

        this.rectd.draw_debug = false

        for (let i = 0; i < aVec.length; i += 3) {
          // fill 'tri' with a complete triangle's coords.
          tri.set(aVec[i], aVec[i + 1], aVec[i + 2]) // note: updating 'tri' will automatically update 'edges' & '_verts' (both by ref)

          let thisPlane: THREE.Plane = new THREE.Plane()
          thisPlane = tri.getPlane(thisPlane);
          if (thisPlane != testPlane) {
            testPlane = thisPlane
            tmpPlane = thisPlane
            rectangleCorners = []
            if (!cornersDegenerate(testPlane)) {
              funcAddCornersFromPlate(rectangleCorners, testPlane)
            }
            this.initRoundedCorners(roundedCorners, rectangleCorners)
          }

          // if (tri.getArea() <= 0.001) continue;
          //==========================================================================================
          // Start of 'adding vertices to newVerts' for each triangle
          //
          let newVerts = [] // vertices in random order, as they are added.
          let listVecs = [] // vertices ordered in consecutive groups of three (triangles), resulting from triangulation of 'newVerts'.
          let setPlanesIntersected = new Set()
          let listCutCornersIntersected = []

          // add vertices inside frustum
          // for (let v of _verts) {
          let triVerts = [tri.a, tri.b, tri.c]
          for (let v of triVerts) {
            // Prepare World Vertex for robust checking
            const vWorld = v.clone().applyMatrix4(obj.matrixWorld);

            if (clipper.isPointInside(vWorld))
              // if vertex is inside all 4 planes
              newVerts.push(v)
          }

          if (newVerts.length == 3) {
            // all triangle is inside frustum
            listVecs[0] = sideSign > 0 ? [tri.a, tri.b, tri.c] : [tri.a, tri.c, tri.b]
          } else {
            // 'listCornersInPlate' is being built here with 4 positions, one for each corner, indicating whether a rounded corner
            //   could be appendend at the end of each plate processing.
            // for each corner, given a triangle formed by the rectangle's corner and the two vertices of the corresponding
            //   cut corner's edge, test if all of these 3 vertices are present in the current plate (ie. several tris)
            // the result (after processing all plate's tris) for each of the 4 positions in the list is a number between 0 and 7.
            //   If it is 7 then all 3 vertices are inside the plate.
            //
            // NOTE: 'listCornersInPlate' will only be ready for use after done processing all plate's tris.
            //

            roundedCorners.updateListCornersInPlate(listCornersInPlate, rectangleCorners, tri);
            // keep all tri's edges intersecting the edges of rect's cut corners for post processing below.
            roundedCorners.intersectTriangleEdgesWithCuts(edges, listCutCornersIntersected);

            // add all vertices of tri's edges intersecting planes (planes = rectangle's edges)
            let tmpVec = new THREE.Vector3()
            let edgesIntersec: [Vector3, string][] = []
            let allEdgesIntersec: [Vector3, string, number, number, number, number][] = []
            for (let indPlane in planes) {
              for (let edge of edges) {
                if (planes[indPlane].intersectsLine(edge)) {
                  let point: Vector3 = planes[indPlane].intersectLine(edge, tmpVec)
                  let d0 = planes[0].distanceToPoint(point)
                  let d1 = planes[1].distanceToPoint(point)
                  let d2 = planes[2].distanceToPoint(point)
                  let d3 = planes[3].distanceToPoint(point)
                  allEdgesIntersec.push([point.clone(), indPlane, d0, d1, d2, d3])

                  let closestDistance: number = null
                  for (let index of listAllOtherPlanes[indPlane]) {
                    let d = planes[index].distanceToPoint(point)
                    if (closestDistance == null || d < closestDistance) {
                      closestDistance = d
                    }
                  }
                  // if (planes.every((pl) => pl.distanceToPoint(point) >= -0.001)){
                  if (closestDistance >= -0.001) {
                    newVerts.push(point.clone())
                    edgesIntersec.push([point.clone(), indPlane])
                    setPlanesIntersected.add(indPlane)
                  }
                }
              }
            }

            for (let corner of rectangleCorners) {
              if (tri.containsPoint(corner)) {
                newVerts.push(corner)
              }
            }

            if (newVerts.length) {
              let retVerts = RepairGeometryUtils.createPartialTriangles(this.graphics.camera, newVerts, sectNormal, sideSign, this.rectd.shapeType, tri, (verts) => { if (this.rectd.draw_debug) this.draw_debug_FloatArray(verts.flatMap(v => [v.x, v.y, v.z]), false, false) });
              listVecs[0] = retVerts;
            }

            // }
          }

          if (!Util.isNullOrUndefined(listVecs[0]) && listVecs[0].length) {



            //========================================
            let listCoords = listVecs[0].flatMap((vec) => [vec.x, vec.y, vec.z])
            if (Util.isNullOrUndefined(listPos[0])) listPos[0] = []
            listPos[0].push(...listCoords)
            listTrisInPlate.push(...listCoords)
            //----------------------------------------
            //let listVecsNormals = [...listVecs]
            let listVecsNormals = listVecs[0].map((v) => v.clone())
            //listVecsNormals.map((v) => v.add(sectNormal.clone().multiplyScalar(offsetGeom)))
            let listCoordsNormals = listVecsNormals.flatMap((vec) => [vec.x, vec.y, vec.z])
            listPosNormals.push(...listCoordsNormals)
            listTrisInPlateNormals.push(...listCoordsNormals)
            //========================================
          }

          //
          // end of 'adding vertices to newVerts'
          //==========================================================================================
        } // for aPos length

        if (listTrisInPlate.length > 0 && targets.every(x => x.uuid != obj.uuid)) {
          targets.push(obj)
          // console.log('objeto adicionado: ', obj.name)
          listPosPerPlate.push(listTrisInPlate)
          listPosPerPlateNormals.push(listTrisInPlateNormals)

          // if (GraphicsRenderRepair.isSplit) {
          //   targets = [obj]
          //   this.meshIndex++;
          //   this.finalizeRepair(listCornersInPlate, listPlateArcsForAllPlates, sectNormalAllPlates, sideSign, listPos, listPosPerPlate, listPosNormals, listPosPerPlateNormals
          //     , targets)
          // }
        }
      } // if visible && "plate"
    }
    // if(this.asset.)
    // allInpectionElements?.forEach((point) => {
    //   if (
    //     (planes.every((pl) => pl.distanceToPoint(point.SurfaceDesign.position) >= 0) || planes.every((pl) => pl.distanceToPoint(point.SurfaceDesign.position) <= 0)) &&
    //     (point as GaugingElement).inspectionMethodId == INSERTIONCONSTANTS.POINT
    //     // (point as GaugingElement).inspectionMethodId == "cca40b09-d671-4cd4-9001-1d5c7ec863cb"
    //   ) {
    //     this.inspectionsInsideArea.push((point as GaugingElement));
    //   }
    // })
    if (!GraphicsRenderRepair.isSplit && listPos.length > 0) {
      // this.meshIndex++;
      this.finalizeRepairCut(listCornersInPlate, listPlateArcsForAllPlates, sectNormalAllPlates, sideSign, listPos,
        listPosPerPlate, listPosNormals, listPosPerPlateNormals
        , targets, index)
    }
  }
  public finalizeRepairCut(listCornersInPlate: number[], listPlateArcsForAllPlates: any[], sectNormalAllPlates: THREE.Vector3,
    sideSign: number, listPos: any[], listPosPerPlate: any[], listPosNormals: any[], listPosPerPlateNormals: any[]
    , targets: any[], index) {

    let listVecs = []
    // this.rectd.meshs = [];
    targets[0]?.geometry.computeBoundingBox() // create an bounding box to check if roundedEdge is inside the target's geometry
    var vector = new THREE.Vector3();
    if (this.orthoFrame) {
      vector.copy(this.orthoFrame.n).negate()
    } else {
      this.graphics.camera.getWorldDirection(vector)
    }

    for (let indRect in listCornersInPlate) { // circularize cuts made at rectangle's vertices
      if (listCornersInPlate[indRect] == 7) {  // but only if the entire cut is inside the target
        // @TODO: create partial arc geometry acording to target intersection

        let arcs = listPlateArcsForAllPlates[indRect]
        let raycastedPoint = []
        if (arcs != undefined) {
          for (let arc of arcs) {
            for (let target of targets) {
              let rayc = new THREE.Raycaster(arc, vector)
              let point = rayc.intersectObject(target)
              if (!Util.isNullOrUndefined(point))
                raycastedPoint.push(point[0].point)
              rayc = new THREE.Raycaster(arc, vector.reflect(vector))
              let npoint = rayc.intersectObject(target)
              if (!Util.isNullOrUndefined(npoint))
                raycastedPoint.push(npoint[0].point)
            }
          }
        }

        let flag = GraphicsRenderRepair.isSplit ? raycastedPoint.every(arc => targets[0].geometry.boundingBox.containsPoint(arc)) : true
        if (flag) {
          let vertsRoundCorners = RepairGeometryUtils.createPartialTriangles(this.graphics.camera, raycastedPoint, sectNormalAllPlates, sideSign, this.rectd.shapeType, undefined, (verts) => { if (this.rectd.draw_debug) this.draw_debug_FloatArray(verts.flatMap(v => [v.x, v.y, v.z]), false, false) });
          listVecs.push(...vertsRoundCorners)
        }
      }
    }

    let listCoords = listVecs.flatMap((vec) => [vec.x, vec.y, vec.z])
    listPos[0].push(...listCoords)
    listPosPerPlate.push(listCoords)
    let listVecsNormals = listVecs.map((v) => v.clone())
    let listCoordsNormals = listVecsNormals.flatMap((vec) => [vec.x, vec.y, vec.z])
    listPosNormals.push(...listCoordsNormals)
    listPosPerPlateNormals.push(listCoordsNormals)

    // for (let ind = 0; ind < this.meshIndex; ind++) { // meshIndex != 0 if the repair is Splitted
    if (listPos[0].length) this.drawFloatArrayRepairCut(listPos[0], listPosPerPlateNormals, { color: 0xff0000 }, targets, index)
    // }

  }



  private processRepairObject(
    clipper: RepairGeometryClipper,
    workableElement: WorkableElement,
    planes: THREE.Plane[],
    targets: any[],
    listPos: any[],
    listPosPerPlate: any[],
    listPosNormals: any[],
    listPosPerPlateNormals: any[],
    listCornersInPlate: any[],
    listPlateArcsForAllPlates: any[],
    sectNormalAllPlates: THREE.Vector3
  ) {
    // was: const isNewModelRegions = this.asset?.isNewModelMode && this.asset?.mode === 'REGIONS';
    // was: if (isNewModelRegions || workableElement.realVisibility) {
    if (this.isNewModelRegions || workableElement.realVisibility) {
      if (workableElement.TypeId !== GraphicsRenderRepair.targetTypeId) {
        return;
      }
    }




    const obj = workableElement.SurfaceDesign as any;

    let sectNormal = workableElement.elementNormal ? workableElement.elementNormal : workableElement.section.sectionNormal;
    let aPos = obj.geometry.attributes.position.array
    let aInd = obj.geometry.index

    let geoNormal = obj.userData.GeoNormal
    let pos = obj.position

    let offsetPos = false;

    let aVec = []

    if (aInd != null) {
      for (let i of aInd.array) {
        let x = aPos[i * 3],
          y = aPos[i * 3 + 1],
          z = aPos[i * 3 + 2]
        aVec.push(offsetPos ? new THREE.Vector3(z, y, x).add(pos) : new THREE.Vector3(x, y, z))
      }
    } else {
      for (let i = 0; i < aPos.length; i += 3) {
        let x = aPos[i],
          y = aPos[i + 1],
          z = aPos[i + 2]
        aVec.push(offsetPos ? new THREE.Vector3(z, y, x).add(pos) : new THREE.Vector3(x, y, z))
      }
    }

    if (aVec.length === 0 || aVec.length < 3)
      return;

    let listTrisInPlate = []
    let listTrisInPlateNormals = []

    sectNormalAllPlates.set(sectNormal.x, sectNormal.y, sectNormal.z);

    let rectangleCorners: Vector3[] = []

    let tri = new THREE.Triangle();
    let _verts = [tri.a, tri.b, tri.c];
    // This was simplified in original loop but here we need to reconstruct what was used
    // Wait, _verts is reused.
    // In original code:
    // _verts.forEach((v, ind) => v.copy(aVec[ind]))
    _verts[0].copy(aVec[0]);
    _verts[1].copy(aVec[1]);
    _verts[2].copy(aVec[2]);

    let tmpPlane = new THREE.Plane()
    let counter = 1;

    let testPlane = tri.getPlane(tmpPlane);
    testPlane.setFromCoplanarPoints(tri.a, tri.b, tri.c)

    const canonicalDirection = Util.LargestCanonicalAxisDirection(workableElement.section.sectionNormal)
    while ((testPlane.constant === 0 || testPlane.constant === -0) && testPlane.normal.equals(new THREE.Vector3()) && counter < aVec.length / 3) {
      _verts[0] = aVec[counter * 3];
      _verts[1] = aVec[counter * 3 + 1];
      _verts[2] = aVec[counter * 3 + 2];
      testPlane = tri.getPlane(tmpPlane);

      if (canonicalDirection === 'x')
        testPlane.constant = testPlane.constant * -1;
      counter++;
    }
    let centerBbox = new THREE.Vector3();
    if ((testPlane.constant === 0 || testPlane.constant === -0) && testPlane.normal.equals(new THREE.Vector3())) {
      workableElement.SurfaceDesign.geometry.boundingBox.getCenter(centerBbox)
      if (canonicalDirection === 'x')
        testPlane = new THREE.Plane(workableElement.section.sectionNormal, centerBbox.x)
      else if (canonicalDirection === 'y')
        testPlane = new THREE.Plane(workableElement.section.sectionNormal, centerBbox.y)
      else if (canonicalDirection === 'z')
        testPlane = new THREE.Plane(workableElement.section.sectionNormal, centerBbox.z)
      if (canonicalDirection === 'x')
        testPlane.constant = testPlane.constant * -1;
    }

    // Inline funcAddCornersFromPlate logic as it accesses local variables in original scope
    // But wait, funcAddCornersFromPlate was defined inside createDecalGeometry.
    // I need to replicate that logic or pass it?
    // It was:
    // let funcAddCornersFromPlate = (listTarget, platePlane) =>
    //   listAdjacentPlanes.forEach((planePair) =>
    //     listTarget.push(
    //       RepairGeometryUtils.vertIntersectPlanes(platePlane, planes[planePair[0]], planes[planePair[1]]),
    //     ),
    //   )
    // I can implement it here.
    let listAdjacentPlanes = [[0, 1], [1, 2], [2, 3], [3, 0]];
    // with prism planes (ortho mode), corner intersection degenerates when the surface
    // plane contains the projection direction (element edge-on to the prism)
    const cornersDegenerate = (plane: THREE.Plane) =>
      this.orthoFrame != null && Math.abs(plane.normal.dot(this.orthoFrame.n)) < 0.1;

    if (!cornersDegenerate(testPlane)) {
      listAdjacentPlanes.forEach((planePair) =>
        rectangleCorners.push(
          RepairGeometryUtils.vertIntersectPlanes(testPlane, planes[planePair[0]], planes[planePair[1]]),
        ),
      )
    }

    const roundedCorners = new RepairRoundedCorners();
    this.initRoundedCorners(roundedCorners, rectangleCorners);

    this.rectd.draw_debug = false

    let fline = (p1, p2) => new THREE.Line3(p1, p2)
    let edges = [fline(tri.a, tri.b), fline(tri.b, tri.c), fline(tri.c, tri.a)]
    let listAllOtherPlanes = [[1, 2, 3], [0, 2, 3], [0, 1, 3], [0, 1, 2]];
    let sideSign = 0; // Assuming 0 as per initialized value in main method

    const _camDir = this.graphics.camera.getWorldDirection(new THREE.Vector3());
    const projDir = this.orthoFrame ? this.orthoFrame.n : _camDir;
    const isEdgeOnPlate = Math.abs(sectNormal.dot(projDir)) < 0.1;

    // world-space bounds of this element, used to start ortho raycasts outside the geometry
    let objWorldBoxDiag = 0;
    let objWorldBoxCenter: THREE.Vector3 = null;
    if (this.orthoFrame) {
      const objWorldBox = new THREE.Box3().setFromObject(obj);
      objWorldBoxDiag = objWorldBox.getSize(new THREE.Vector3()).length() || 1000;
      objWorldBoxCenter = objWorldBox.getCenter(new THREE.Vector3());
    }

    for (let i = 0; i < aVec.length; i += 3) {
      tri.set(aVec[i], aVec[i + 1], aVec[i + 2])

      let thisPlane: THREE.Plane = new THREE.Plane()
      thisPlane = tri.getPlane(thisPlane);

      // Removed simplified backface culling as it caused issues with camera positioning

      if (thisPlane != testPlane) {
        testPlane = thisPlane
        tmpPlane = thisPlane
        rectangleCorners = []
        // Re-run corner adding
        if (!cornersDegenerate(testPlane)) {
          listAdjacentPlanes.forEach((planePair) =>
            rectangleCorners.push(
              RepairGeometryUtils.vertIntersectPlanes(testPlane, planes[planePair[0]], planes[planePair[1]]),
            ),
          )
        }
        this.initRoundedCorners(roundedCorners, rectangleCorners)
      }

      let newVerts = []
      let listVecs = []
      let setPlanesIntersected = new Set()
      let listCutCornersIntersected = []

      let triVerts = [tri.a, tri.b, tri.c]

      if (this.rectd.shapeType === 'circle' && obj.matrixWorld) {
        obj.updateMatrixWorld(true)
      }

      for (let v of triVerts) {
        let isInside = false
        const vWorld = v.clone().applyMatrix4(obj.matrixWorld);

        let checkParams = undefined;
        // ortho mode dispatches to the frame's prism/cylinder test — screen params unused
        if (!this.orthoFrame) {
          if (this.rectd.shapeType === 'circle' && this.rectd.centerX !== undefined && this.rectd.centerY !== undefined && this.rectd.radius !== undefined) {
            if (!obj.geometry.boundingBox) obj.geometry.computeBoundingBox();
            checkParams = {
              centerX: this.rectd.centerX,
              centerY: this.rectd.centerY,
              radius: this.rectd.radius,
              objectWorldMatrix: obj.matrixWorld,
              objectBoundingBox: obj.geometry.boundingBox
            };
          } else if (this.rectd.shapeType === 'rectangle' && rectangleCorners && rectangleCorners.length === 4) {
            if (!isEdgeOnPlate) {
              checkParams = {
                centerX: 0,
                centerY: 0,
                radius: 0,
                rectangleCorners: rectangleCorners
              };
            }
          }
        }

        isInside = clipper.isPointInside(vWorld, checkParams);

        // Raycast Occlusion Check
        // If point is inside selection area but is occluded by geometry in front, discard it.
        // This solves "spiking" where back-facing vertices are projected inside the shape.
        if (isInside) {
          const raycaster = new THREE.Raycaster();
          if (this.orthoFrame) {
            // With a prism/cylinder volume the plate's back face is genuinely inside,
            // so occlusion is tested along the projection normal instead of the view ray:
            // keep only the +n-facing surface (n is oriented toward the camera).
            const rayStart = vWorld.clone().add(this.orthoFrame.n.clone().multiplyScalar(objWorldBoxDiag));
            raycaster.set(rayStart, this.orthoFrame.n.clone().negate());
            const intersects = raycaster.intersectObject(obj, false);
            if (intersects.length > 0 && intersects[0].distance < objWorldBoxDiag - 0.0001) {
              isInside = false;
            }
          } else {
            const direction = vWorld.clone().sub(this.graphics.camera.position).normalize();
            raycaster.set(this.graphics.camera.position, direction);
            const intersects = raycaster.intersectObject(obj, false);

            if (intersects.length > 0) {
              const distToVertex = vWorld.distanceTo(this.graphics.camera.position);
              // Strict check: if ANY hit is closer, the vertex is occluded
              // Use very small epsilon to account for floating point precision only
              if (intersects[0].distance < distToVertex - 0.0001) {
                isInside = false;
              }
            }
          }
        }

        if (isInside) {
          newVerts.push(v)
        }
      }

      if (newVerts.length == 3) {
        listVecs[this.meshIndex] = sideSign > 0 ? [tri.a, tri.b, tri.c] : [tri.a, tri.c, tri.b]
      } else {

        if (this.rectd.shapeType !== 'circle' && this.rectd.shapeType !== 'ellipse') {
          roundedCorners.updateListCornersInPlate(listCornersInPlate, rectangleCorners, tri);
          roundedCorners.intersectTriangleEdgesWithCuts(edges, listCutCornersIntersected);
        }

        let tmpVec = new THREE.Vector3()
        let edgesIntersec: [Vector3, string][] = []
        let allEdgesIntersec: [Vector3, string, number, number, number, number][] = []

        const isCircle = this.rectd.shapeType === 'circle';
        const isEllipse = this.rectd.shapeType === 'ellipse';
        const isCircleOrEllipse = isCircle || isEllipse;
        const hasCircleParams = isCircle && this.rectd.centerX !== undefined && this.rectd.centerY !== undefined && this.rectd.radius !== undefined;
        const hasEllipseParams = isEllipse && this.rectd.centerX !== undefined && this.rectd.centerY !== undefined && this.rectd.radiusX !== undefined && this.rectd.radiusY !== undefined;

        if ((hasCircleParams || hasEllipseParams) && obj.matrixWorld) {
          const worldToLocalMatrix = obj.matrixWorld.clone().invert();
          for (let edge of edges) {
            const edgeStartWorld = edge.start.clone().applyMatrix4(obj.matrixWorld)
            const edgeEndWorld = edge.end.clone().applyMatrix4(obj.matrixWorld)

            let intersections: THREE.Vector3[] = [];
            if (this.orthoFrame) {
              intersections = isCircle
                ? this.orthoFrame.segmentCircleIntersections(edgeStartWorld, edgeEndWorld)
                : this.orthoFrame.segmentEllipseIntersections(edgeStartWorld, edgeEndWorld)
            } else if (isCircle) {
              intersections = RepairGeometryUtils.findLineCircleIntersectionsScreenSpace(this.graphics.camera,
                edgeStartWorld, edgeEndWorld, this.rectd.centerX, this.rectd.centerY, this.rectd.radius
              )
            } else {
              intersections = RepairGeometryUtils.findLineEllipseIntersectionsScreenSpace(this.graphics.camera,
                edgeStartWorld, edgeEndWorld, this.rectd.centerX, this.rectd.centerY, this.rectd.radiusX, this.rectd.radiusY
              )
            }

            for (const intersection of intersections) {
              const intersectionLocal = intersection.clone().applyMatrix4(worldToLocalMatrix)
              const isDuplicate = newVerts.some(vert => vert.distanceTo(intersectionLocal) < 0.001)
              if (!isDuplicate) {
                newVerts.push(intersectionLocal)
                edgesIntersec.push([intersectionLocal, this.rectd.shapeType]) // Use shapeType as label
              }
            }
          }

          if (this.orthoFrame) {
            // ortho mode: uv-space bbox overlap, then sample the rectified boundary and
            // drop the samples onto the triangle with rays along -n
            const frame = this.orthoFrame;
            const triUVs = [tri.a, tri.b, tri.c].map(v =>
              frame.toUV(v.clone().applyMatrix4(obj.matrixWorld)))

            let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity
            for (const uv of triUVs) {
              minU = Math.min(minU, uv.u)
              maxU = Math.max(maxU, uv.u)
              minV = Math.min(minV, uv.v)
              maxV = Math.max(maxV, uv.v)
            }

            const shapeRU = isCircle ? frame.radius : frame.radiusU
            const shapeRV = isCircle ? frame.radius : frame.radiusV
            const bboxOverlap = !(maxU < -shapeRU || minU > shapeRU || maxV < -shapeRV || minV > shapeRV)

            if (bboxOverlap) {
              const numSamples = 100
              const targetVec = new THREE.Vector3()
              const rayDirLocal = frame.n.clone().negate().transformDirection(worldToLocalMatrix).normalize()

              for (const pW of frame.sampleOutlineWorld(numSamples)) {
                const uv = frame.toUV(pW)
                if (uv.u < minU || uv.u > maxU || uv.v < minV || uv.v > maxV) continue

                // start the ray beyond the element along +n so it can hit any triangle depth
                const offset = objWorldBoxDiag + Math.abs(objWorldBoxCenter.clone().sub(pW).dot(frame.n))
                const rayOriginLocal = pW.clone()
                  .add(frame.n.clone().multiplyScalar(offset))
                  .applyMatrix4(worldToLocalMatrix)
                const localRay = new THREE.Ray(rayOriginLocal, rayDirLocal.clone())

                const intersect = localRay.intersectTriangle(tri.a, tri.b, tri.c, false, targetVec, 0)

                if (intersect) {
                  const isDuplicate = newVerts.some(vert => vert.distanceTo(targetVec) < 0.0001)
                  if (!isDuplicate) {
                    newVerts.push(targetVec.clone())
                  }
                }
              }
            }
          } else {
          const triVertsScreen = [tri.a, tri.b, tri.c].map(v => {
            const vWorld = v.clone().applyMatrix4(obj.matrixWorld)
            return vWorld.project(this.graphics.camera)
          })

          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
          for (const v of triVertsScreen) {
            const sx = (v.x * 0.5 + 0.5) * window.innerWidth
            const sy = (-v.y * 0.5 + 0.5) * window.innerHeight
            minX = Math.min(minX, sx)
            maxX = Math.max(maxX, sx)
            minY = Math.min(minY, sy)
            maxY = Math.max(maxY, sy)
          }

          let shapeMinX, shapeMaxX, shapeMinY, shapeMaxY;
          if (isCircle) {
            shapeMinX = this.rectd.centerX - this.rectd.radius;
            shapeMaxX = this.rectd.centerX + this.rectd.radius;
            shapeMinY = this.rectd.centerY - this.rectd.radius;
            shapeMaxY = this.rectd.centerY + this.rectd.radius;
          } else {
            shapeMinX = this.rectd.centerX - this.rectd.radiusX;
            shapeMaxX = this.rectd.centerX + this.rectd.radiusX;
            shapeMinY = this.rectd.centerY - this.rectd.radiusY;
            shapeMaxY = this.rectd.centerY + this.rectd.radiusY;
          }

          const bboxOverlap = !(maxX < shapeMinX || minX > shapeMaxX || maxY < shapeMinY || minY > shapeMaxY)

          if (bboxOverlap) {
            // Rough circumference approximation for sampling count
            const approxRadius = isCircle ? this.rectd.radius : Math.max(this.rectd.radiusX, this.rectd.radiusY);
            const circumference = 2 * Math.PI * approxRadius
            const numSamples = Math.max(24, Math.min(200, Math.floor(circumference / 3)))

            const raycaster = new THREE.Raycaster()
            const targetVec = new THREE.Vector3()

            for (let i = 0; i < numSamples; i++) {
              const angle = (i / numSamples) * Math.PI * 2
              let sx, sy;
              if (isCircle) {
                sx = this.rectd.centerX + this.rectd.radius * Math.cos(angle)
                sy = this.rectd.centerY + this.rectd.radius * Math.sin(angle)
              } else {
                sx = this.rectd.centerX + this.rectd.radiusX * Math.cos(angle)
                sy = this.rectd.centerY + this.rectd.radiusY * Math.sin(angle)
              }

              if (sx < minX || sx > maxX || sy < minY || sy > maxY) continue

              const ndcX = (sx / window.innerWidth) * 2 - 1
              const ndcY = (-sy / window.innerHeight) * 2 + 1

              raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.graphics.camera)

              const rayOriginLocal = raycaster.ray.origin.clone().applyMatrix4(worldToLocalMatrix)
              const rayDirLocal = raycaster.ray.direction.clone().transformDirection(worldToLocalMatrix).normalize()
              const localRay = new THREE.Ray(rayOriginLocal, rayDirLocal)

              const intersect = localRay.intersectTriangle(tri.a, tri.b, tri.c, false, targetVec, 0)

              if (intersect) {
                const isDuplicate = newVerts.some(vert => vert.distanceTo(targetVec) < 0.0001)
                if (!isDuplicate) {
                  newVerts.push(targetVec.clone())
                }
              }
            }
          }
          }
        } else {
          for (let indPlane in planes) {
            for (let edge of edges) {
              if (planes[indPlane].intersectsLine(edge)) {
                let point: Vector3 = planes[indPlane].intersectLine(edge, tmpVec)
                let closestDistance: number = null
                for (let index of listAllOtherPlanes[indPlane]) {
                  let d = planes[index].distanceToPoint(point)
                  if (closestDistance == null || d < closestDistance) {
                    closestDistance = d
                  }
                }
                if (closestDistance >= -0.001) {
                  let isValidPoint = true;
                  const hasRoundedCorners = (this.rectd.cornerRadius && this.rectd.cornerRadius > 0);

                  if (hasRoundedCorners && roundedCorners && roundedCorners.params && roundedCorners.params.rectangleCutCorners) {
                    // Check if point lies on the "removed" segment of the edge near any corner.
                    // The removed segments approx map to [corner, cutLine.start] and [corner, cutLine.end]
                    // Since we don't have explicit edge mapping, we check if point is very close to any corner-cut-start/end segment
                    for (let i = 0; i < 4; i++) {
                      const corner = rectangleCorners[i];
                      if (!corner) continue;

                      const cutLine = roundedCorners.params.rectangleCutCorners[i];
                      if (!cutLine) continue;

                      // Robust Corner Filtering:
                      // Check if point is in the "corner quadrant" and outside the arc radius.
                      const center = roundedCorners.getCornerCenter(i, corner);
                      if (center) {
                        const vStart = cutLine.start.clone().sub(center);
                        const vEnd = cutLine.end.clone().sub(center);
                        const radius = vStart.length();

                        const vPoint = point.clone().sub(center);

                        // Check if point effectively lies in the quadrant defined by vStart and vEnd
                        // We normalize to use as basis vectors
                        const basis1 = vStart.clone().normalize();
                        const basis2 = vEnd.clone().normalize();

                        const proj1 = vPoint.dot(basis1);
                        const proj2 = vPoint.dot(basis2);

                        // If both projections are positive, we are "outward" from the center towards the corner
                        // (Assuming vStart and vEnd point from center TO the edges/cuts)
                        // Relax tolerance slightly ( > -epsilon) to catch points exactly on the boundary lines
                        if (proj1 > -0.001 && proj2 > -0.001) {
                          // We are in the corner wedge.
                          // If distance is greater than radius, it's a spike.
                          // Use specific tolerance for curved surfaces -> strict check usually better here
                          if (vPoint.length() > radius + 0.001) {
                            isValidPoint = false;
                            break;
                          }
                        }
                      }
                    }
                  }

                  if (isValidPoint) {
                    newVerts.push(point.clone())
                    edgesIntersec.push([point.clone(), indPlane])
                    setPlanesIntersected.add(indPlane)
                  }
                }
              }
            }
          }
        }

        if (this.rectd.shapeType !== 'circle' && this.rectd.shapeType !== 'ellipse') {
          // Intersect edges with Rounded Corner Arcs
          const hasRoundedCorners = (this.rectd.cornerRadius && this.rectd.cornerRadius > 0);
          if (hasRoundedCorners && roundedCorners) {
            for (let i = 0; i < 4; i++) {
              const corner = rectangleCorners[i];
              if (!corner) continue;

              const center = roundedCorners.getCornerCenter(i, corner);
              if (!center) continue;

              // Radius can be derived from center distance to any cut point (e.g. cutLine.start)
              const cutLine = roundedCorners.params.rectangleCutCorners[i];
              if (!cutLine) continue;

              const radius = cutLine.start.distanceTo(center);

              // Check intersections with this corner's circle
              for (let edge of edges) {
                const circleIntersections = RepairGeometryUtils.findLineSphereIntersections(edge, center, radius);
                for (let p of circleIntersections) {
                  // Check if point is on the valid 90-degree arc
                  // The valid arc is "in between" the vectors from center to cutLine.start and cutLine.end
                  const toP = p.clone().sub(center);
                  const toStart = cutLine.start.clone().sub(center);
                  const toEnd = cutLine.end.clone().sub(center);

                  // Check positive dot product with both basis vectors (simple quadrant check)
                  // Since toStart and toEnd are orthogonal and define the sector
                  if (toP.dot(toStart) >= -0.001 && toP.dot(toEnd) >= -0.001) {
                    // Additionally check if the point is actually close to the plane of the plate?
                    // Our edges are on the triangle on the plate, so yes.

                    // Avoid duplicates
                    const isDuplicate = newVerts.some(vert => vert.distanceTo(p) < 0.001);
                    if (!isDuplicate) {
                      newVerts.push(p.clone());
                      edgesIntersec.push([p.clone(), this.rectd.shapeType]);
                    }
                  }
                }
              }
            }
          }

          // Check corners. If using rounded corners (cornerRadius > 0), we do NOT add the sharp corner.
          // Instead we add the endpoints of the cut line if they are inside.

          for (let i = 0; i < rectangleCorners.length; i++) {
            let corner = rectangleCorners[i];
            // If not rounding this corner, or if logic dictates, add it.
            // But here we assume all 4 are rounded if cornerRadius is set.
            if (!hasRoundedCorners) {
              if (tri.containsPoint(corner)) {
                newVerts.push(corner)
              }
            } else {
              if (roundedCorners && roundedCorners.params && roundedCorners.params.rectangleCutCorners[i]) {
                const cutLine = roundedCorners.params.rectangleCutCorners[i];
                if (tri.containsPoint(cutLine.start)) newVerts.push(cutLine.start.clone());

                // Add arc points
                const arcPoints = roundedCorners.getCornerArcPoints(i, corner);
                for (const p of arcPoints) {
                  if (tri.containsPoint(p)) newVerts.push(p);
                }

                if (tri.containsPoint(cutLine.end)) newVerts.push(cutLine.end.clone());
              }
            }
          }

          // Add intersections from rounded corners cuts
          if (listCutCornersIntersected && listCutCornersIntersected.length > 0) {
            for (let sublist of listCutCornersIntersected) {
              for (let point of sublist) {
                const isDuplicate = newVerts.some(vert => vert.distanceTo(point) < 0.001);
                if (!isDuplicate) {
                  newVerts.push(point.clone());
                }
              }
            }
          }
        }

        if (newVerts.length) {
          // Use 'circle' sorting type if we have rounded corners, as it handles general convex polygons (like rounded rects) better than the 'rectangle' linear sort
          const sortType = (this.rectd.cornerRadius && this.rectd.cornerRadius > 0) ? 'circle' : this.rectd.shapeType;
          let retVerts = RepairGeometryUtils.createPartialTriangles(this.graphics.camera, newVerts, sectNormal, sideSign, sortType as any, tri, (verts) => { if (this.rectd.draw_debug) this.draw_debug_FloatArray(verts.flatMap(v => [v.x, v.y, v.z]), false, false) });
          listVecs[this.meshIndex] = retVerts;
        }
      }

      if (!Util.isNullOrUndefined(listVecs[this.meshIndex]) && listVecs[this.meshIndex].length) {
        let listCoords = listVecs[this.meshIndex].flatMap((vec) => [vec.x, vec.y, vec.z])
        if (Util.isNullOrUndefined(listPos[this.meshIndex])) listPos[this.meshIndex] = []
        listPos[this.meshIndex].push(...listCoords)
        listTrisInPlate.push(...listCoords)
        let listVecsNormals = listVecs[this.meshIndex].map((v) => v.clone())
        let listCoordsNormals = listVecsNormals.flatMap((vec) => [vec.x, vec.y, vec.z])
        listPosNormals.push(...listCoordsNormals)
        listTrisInPlateNormals.push(...listCoordsNormals)
      }
    }

    if (listTrisInPlate.length > 0 && targets.every(x => x.uuid != obj.uuid)) {
      targets.push(obj)
      listPosPerPlate.push(listTrisInPlate)
      listPosPerPlateNormals.push(listTrisInPlateNormals)

      if (GraphicsRenderRepair.isSplit) {
        if (Util.isNullOrUndefined(listPos[this.meshIndex])) {
          listPos[this.meshIndex] = []
        }
        this.meshIndex++;
        this.finalizeRepair(listCornersInPlate, listPlateArcsForAllPlates, sectNormalAllPlates, sideSign, listPos, listPosPerPlate, listPosNormals, listPosPerPlateNormals
          , targets)
      }
    }
  }

  private checkInspectionsInsideArea(allInpectionElements: any[], planes: THREE.Plane[]) {
    allInpectionElements?.forEach((point) => {
      let isInside = false

      if (this.orthoFrame) {
        // ortho mode: prism/cylinder test along the element normal, all shapes
        isInside = this.orthoFrame.isInside(point.SurfaceDesign.position)
      } else if (this.rectd.shapeType === 'circle' && this.rectd.centerX !== undefined && this.rectd.centerY !== undefined && this.rectd.radius !== undefined) {
        // For circles, use screen-space projection for more reliable detection
        isInside = RepairGeometryUtils.isPointInsideCircleScreenSpace(this.graphics.camera, point.SurfaceDesign.position, this.rectd.centerX, this.rectd.centerY, this.rectd.radius)
      } else {
        // For rectangles, check if point is inside all 4 planes
        isInside = (planes.every((pl) => pl.distanceToPoint(point.SurfaceDesign.position) >= 0) || planes.every((pl) => pl.distanceToPoint(point.SurfaceDesign.position) <= 0))
      }

      if (
        isInside &&
        (point as GaugingElement).inspectionMethodId == INSERTIONCONSTANTS.POINT
        // (point as GaugingElement).inspectionMethodId == "cca40b09-d671-4cd4-9001-1d5c7ec863cb"
      ) {
        this.inspectionsInsideArea.push((point as GaugingElement));
      }
    })
  }

  public drawFloatArrayRepairCut(listPos, listPosPerPlate: number[][], mat, targets, index) {

    const offs = 0.01 // Offset from plane
    // if (!GraphicsRenderRepair.isSplit) this.rectd.meshsAfterCut = [];

    // if (this.rectd.singleGeometry) {
    let listposOffset = listPos // listPos.map(x => x - offs)

    let geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(listposOffset), 3))
    geom.computeVertexNormals();
    geom.computeBoundingBox();
    // material.depthWrite = false;
    // targets[0].material.depthWrite = false;
    let mesh = new THREE.Mesh(geom, GraphicsMaterials.hatchingMaterial)
    this.rectd.meshsAfterCut[index].push(mesh);
    // this.rectd.decalGroup.add(mesh)
    // console.log("Mesh de reparo: ")
    // console.log(mesh)
    // this.rectd.meshsAfterCut.push(mesh);
    //if(GraphicsRenderRepair.isSplit){
    //  this.targetsAux.push(targets);
    //  this.rectd.targets = this.targetsAux;
    //}else{
    // this.rectd.targets = targets;
    // this.rectd.inspections = this.inspectionsInsideArea;
    // console.log("Targets: " + targets)
    // console.log("Meshs: " + this.rectd.meshs)
    //}
    // } else {
    //   for (let ind in listPosPerPlate) {
    //     let _ind: number = Number.parseInt(ind)

    //     let listposOffset = listPosPerPlate[ind] //listPosPerPlate[ind].map(x => x - offs)

    //     let listColors = [0xff0000, 0xffff00, 0x00ff00, 0x00ffff, 0x0000ff]
    //     let indColor = _ind % 5
    //     indColor = 1 // temporary - override color above
    //     let cor = listColors[indColor]
    //     let corWireframe = listColors.map((x) => ~x)[indColor] // invert wireframe color, so it will always be visible.

    //     let geom = new THREE.BufferGeometry()
    //     geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(listposOffset), 3))
    //     //----------
    //     // para habilitar o wireframe eh so descomentar a linha a seguir e comentar a proxima
    //     //let material = new THREE.MeshBasicMaterial({ color: (mat.wireframe ? corWireframe : cor), wireframe: mat.wireframe })
    //     // let material = new THREE.MeshBasicMaterial({ color: mat.wireframe ? corWireframe : cor })
    //     let material = this.graphicsMaterial.areaInspecMaterial;
    //     //----------
    //     let mesh = new THREE.Mesh(geom, material)
    //     // this.rectd.decalGroup.add(mesh)
    //     this.rectd.meshs.push(mesh);
    //     // console.log("Mesh de reparo: ")
    //     // console.log(mesh)
    //     if (GraphicsRenderRepair.isSplit) {
    //       this.targetsAux.push(targets);
    //       this.rectd.targets = this.targetsAux;
    //     } else {
    //       this.rectd.targets = targets;
    //     }
    //   }
    // }
  }

  public finalizeRepair(listCornersInPlate: number[], listPlateArcsForAllPlates: any[], sectNormalAllPlates: THREE.Vector3, sideSign: number, listPos: any[], listPosPerPlate: any[], listPosNormals: any[], listPosPerPlateNormals: any[]
    , targets: any[]) {

    let listVecs = []
    this.rectd.meshs = [];
    this.rectd.meshsPerTarget = [];
    // this.rectd.targets = [];
    targets[0]?.geometry.computeBoundingBox() // create an bounding box to check if roundedEdge is inside the target's geometry
    var vector = new THREE.Vector3();
    if (this.orthoFrame) {
      vector.copy(this.orthoFrame.n).negate()
    } else {
      this.graphics.camera.getWorldDirection(vector)
    }

    for (let indRect in listCornersInPlate) { // circularize cuts made at rectangle's vertices
      if (listCornersInPlate[indRect] == 7) {  // but only if the entire cut is inside the target
        // @TODO: create partial arc geometry acording to target intersection

        let arcs = listPlateArcsForAllPlates[indRect]
        let raycastedPoint = []
        if (arcs != undefined) {
          for (let arc of arcs) {
            for (let target of targets) {
              let rayc = new THREE.Raycaster(arc, vector)
              let point = rayc.intersectObject(target)
              if (!Util.isNullOrUndefined(point))
                raycastedPoint.push(point[0].point)
              rayc = new THREE.Raycaster(arc, vector.reflect(vector))
              let npoint = rayc.intersectObject(target)
              if (!Util.isNullOrUndefined(npoint))
                raycastedPoint.push(npoint[0].point)
            }
          }
        }

        let flag = GraphicsRenderRepair.isSplit ? raycastedPoint.every(arc => targets[0].geometry.boundingBox.containsPoint(arc)) : true
        if (flag) {
          let vertsRoundCorners = RepairGeometryUtils.createPartialTriangles(this.graphics.camera, raycastedPoint, sectNormalAllPlates, sideSign, this.rectd.shapeType, undefined, (verts) => { if (this.rectd.draw_debug) this.draw_debug_FloatArray(verts.flatMap(v => [v.x, v.y, v.z]), false, false) });
          listVecs.push(...vertsRoundCorners)
        }
      }
    }

    let listCoords = listVecs.flatMap((vec) => [vec.x, vec.y, vec.z])

    // Ensure the array exists before pushing
    const targetIndex = this.meshIndex - 1
    if (targetIndex >= 0) {
      if (Util.isNullOrUndefined(listPos[targetIndex])) {
        listPos[targetIndex] = []
      }
      if (listCoords.length > 0) {
        listPos[targetIndex].push(...listCoords)
      }
    }

    if (listCoords.length > 0) {
      listPosPerPlate.push(listCoords)
    }

    let listVecsNormals = listVecs.map((v) => v.clone())
    let listCoordsNormals = listVecsNormals.flatMap((vec) => [vec.x, vec.y, vec.z])
    if (listCoordsNormals.length > 0) {
      listPosNormals.push(...listCoordsNormals)
      listPosPerPlateNormals.push(listCoordsNormals)
    }

    for (let ind = 0; ind < this.meshIndex; ind++) { // meshIndex != 0 if the repair is Splitted
      if (listPos[ind] && listPos[ind].length) this.drawFloatArray(listPos[ind], listPosPerPlateNormals, { color: 0xff0000 }, targets)
    }

  }
  public drawFloatArray(listPos, listPosPerPlate: number[][], mat, targets?) {

    const offs = 0.01 // Offset from plane
    if (!GraphicsRenderRepair.isSplit) {
      this.rectd.meshs = []
      this.rectd.meshsPerTarget = []
      this.rectd.targets = []
    };

    if (this.rectd.singleGeometry) {
      let listposOffset = listPos // listPos.map(x => x - offs)

      let geom = new THREE.BufferGeometry()
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(listposOffset), 3))
      geom.computeVertexNormals();
      // material.depthWrite = false;
      // targets[0].material.depthWrite = false;
      let mesh = new THREE.Mesh(geom, GraphicsMaterials.hatchingMaterial)
      // this.rectd.decalGroup.add(mesh)
      // console.log("Mesh de reparo: ")
      // console.log(mesh)
      this.rectd.meshs.push(mesh);
      //if(GraphicsRenderRepair.isSplit){
      //  this.targetsAux.push(targets);
      //  this.rectd.targets = this.targetsAux;
      //}else{
      for (const target of targets) {
        let tIndex = this.rectd.targets.findIndex(x => x.name === target.name);
        if (tIndex === -1) {
          this.rectd.targets.push(target);
        }
      }
      // this.rectd.targets.push(...targets);
      this.rectd.inspections = this.inspectionsInsideArea;
      // console.log("Targets: " + targets)
      // console.log("Meshs: " + this.rectd.meshs)
      //}
      let index = 0;
      for (let ind in listPosPerPlate) {
        let _ind: number = Number.parseInt(ind)

        let listposOffset = listPosPerPlate[ind] //listPosPerPlate[ind].map(x => x - offs)

        let listColors = [0xff0000, 0xffff00, 0x00ff00, 0x00ffff, 0x0000ff]
        let indColor = _ind % 5
        indColor = 1 // temporary - override color above
        let cor = listColors[indColor]
        let corWireframe = listColors.map((x) => ~x)[indColor] // invert wireframe color, so it will always be visible.

        let geom = new THREE.BufferGeometry()
        geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(listposOffset), 3))
        //----------
        // para habilitar o wireframe eh so descomentar a linha a seguir e comentar a proxima
        //let material = new THREE.MeshBasicMaterial({ color: (mat.wireframe ? corWireframe : cor), wireframe: mat.wireframe })
        // let material = new THREE.MeshBasicMaterial({ color: mat.wireframe ? corWireframe : cor })
        let material = GraphicsMaterials.areaInspecMaterial;
        //----------
        let mesh = new THREE.Mesh(geom, material)
        mesh.name = targets[index].name;
        index++;
        // this.rectd.decalGroup.add(mesh)
        this.rectd.meshsPerTarget.push(mesh);
        // console.log("Mesh de reparo: ")
        // console.log(mesh)
      }
    } else {
      for (let ind in listPosPerPlate) {
        let _ind: number = Number.parseInt(ind)

        let listposOffset = listPosPerPlate[ind] //listPosPerPlate[ind].map(x => x - offs)

        let listColors = [0xff0000, 0xffff00, 0x00ff00, 0x00ffff, 0x0000ff]
        let indColor = _ind % 5
        indColor = 1 // temporary - override color above
        let cor = listColors[indColor]
        let corWireframe = listColors.map((x) => ~x)[indColor] // invert wireframe color, so it will always be visible.

        let geom = new THREE.BufferGeometry()
        geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(listposOffset), 3))
        //----------
        // para habilitar o wireframe eh so descomentar a linha a seguir e comentar a proxima
        //let material = new THREE.MeshBasicMaterial({ color: (mat.wireframe ? corWireframe : cor), wireframe: mat.wireframe })
        // let material = new THREE.MeshBasicMaterial({ color: mat.wireframe ? corWireframe : cor })
        let material = GraphicsMaterials.areaInspecMaterial;
        //----------
        let mesh = new THREE.Mesh(geom, material)
        // this.rectd.decalGroup.add(mesh)
        this.rectd.meshs.push(mesh);
        // console.log("Mesh de reparo: ")
        // console.log(mesh)
        if (GraphicsRenderRepair.isSplit) {
          this.targetsAux.push(targets);
          this.rectd.targets = this.targetsAux;
        } else {
          this.rectd.targets = targets;
        }
      }
    }
  }

  public draw_debug_FloatArray(listPos, showJustOnce, keepTriangles) {
    if (!this.rectd.draw_debug_prim) {
      if (showJustOnce) this.rectd.draw_debug_prim = true

      let listposOffset = listPos.map((x) => x /*- 0.01*/)

      let geom = new THREE.BufferGeometry()
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(listposOffset), 3))
      let material1 = GraphicsMaterials.greenDebugMaterial;
      let material2 = GraphicsMaterials.blueWireframeDebugMaterial;
      let mesh1 = new THREE.Mesh(geom, material1)
      let mesh2 = new THREE.Mesh(geom, material2)
      this.rectd.decalGroup.add(mesh1, mesh2)
      this.graphics.camera.add(this.rectd.mesh)
      this.graphics.Controls.update()
      if (!keepTriangles) {
        ThreeJsCleanup.disposeObject(mesh1);
        ThreeJsCleanup.disposeObject(mesh2);
        // this.rectd.decalGroup.remove(mesh1, mesh2)
        ThreeJsCleanup.disposeObject(this.rectd.mesh);
        // this.graphics.camera.remove(this.rectd.mesh)
      }
    }
  }


  public screenToLocal(screenX, screenY, localZ) {
    this._pooledNdc.set(
      (screenX / window.innerWidth) * 2 - 1,
      (-screenY / window.innerHeight) * 2 + 1
    );
    this._pooledRaycaster.setFromCamera(this._pooledNdc, this.graphics.camera);
    let newpos = new THREE.Vector3();
    this._pooledRaycaster.ray.at(localZ, newpos);
    this.graphics.camera.worldToLocal(newpos);
    return newpos;
  }

}
